import { Component, OnDestroy, OnInit, AfterViewInit, HostListener, Input, Output, EventEmitter, ViewEncapsulation, ViewChild, ElementRef, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { CompositeKey, EntityFieldValueInfo, EntityInfo, Metadata, RunView, UserInfo } from '@memberjunction/core';
import { UUIDsEqual } from '@memberjunction/global';
import { GraphQLActionClient, GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { mjBizAppsCommonOrganizationEntity } from '@mj-biz-apps/common-entities';
import { IzzyChannelMessageEntity, IzzyChannelMessageNoteEntity, ChannelMessageEntityExtendedClient, IzzyMetadataEngine, BuildChannelMessageSearch, ChannelMessageSearch, ChannelMessageSearchMatches } from 'mj_generatedentities';
import { BCSaaSChannelEntity } from '@bluecypress/bcsaas-entities';
import { MessageEditorComponent } from '../message-editor/message-editor.component';
import { BCMembershipService } from '@bluecypress/bcsaas-entities-extended';
import { BC_ROLE_LEVELS } from '@bluecypress/bcsaas-core';
import * as HtmlDiffModule from 'htmldiff-js';
const HtmlDiff = HtmlDiffModule.default;
import { marked } from 'marked';
import { BaseResourceComponent, NavigationService } from '@memberjunction/ng-shared';
import { RegisterClass } from '@memberjunction/global';
import { ResourceData, MJUserSettingEntity } from '@memberjunction/core-entities';
import { ContentChangeEvent, SaveEvent, EditorMode } from '../message-editor/message-editor.component';
import { categorizeError } from '../../utils/error-categorizer';
import { IzzyAuthService } from '../../services/izzy-auth.service';
import { IzzyPresenceService } from '../../services/izzy-presence.service';
import { AttachmentDrawerComponent } from '../message-attachments/attachment-drawer.component';
import { MessageAttachmentChip } from '../message-attachments/message-attachments.component';

/** Default environment ID seeded by MJ. Matches __mj.Environment.Default. */
const DEFAULT_ENVIRONMENT_ID = 'F51358F3-9447-4176-B313-BF8025FD8D09';

/**
 * State interface for persisting component state
 */
interface MessageApprovalState {
  approvalStatusFilter: string;
  generationStatusFilter: string;
  autoAdvance: boolean;
  selectedMessageId?: string;
  isPanelCollapsed: boolean;
  panelWidth: number;
  selectedOrganizationId?: string;
  selectedChannelId?: string;
  sortMode?: 'received' | 'updated';
  queueFilter?: 'mine' | 'unassigned' | 'all';
  notesFilter?: NotesFilter;
}

/** Internal-notes list filter: every message, only messages with at least one note, or only those without. */
type NotesFilter = 'all' | 'with' | 'without';

/**
 * Interface for interaction events emitted by the component
 */
interface MessageApprovalInteraction {
  type: 'selectMessage' | 'openMessage' | 'approvalChange';
  messageId?: string;
  newStatus?: string;
}

/**
 * Interface for message stats
 */
interface MessageStats {
  pending: number;
  approved: number;
  rejected: number;
  notRequired: number;
  escalate: number;
  total: number;
}


/**
 * Lightweight data for the message list view.
 * Full ChannelMessageEntity objects are loaded on-demand for the detail panel.
 */
/**
 * Lightweight data for the message list view.
 * Full ChannelMessageEntity objects are loaded on-demand for the detail panel.
 *
 * Assignment fields (AssignedToPersonID, AssignedToContact) are defined in the
 * intersection type because they are added by migration + CodeGen and may not yet
 * exist on the generated entity class. After CodeGen runs, these can be moved into the Pick.
 */
type MessageListItem = Pick<IzzyChannelMessageEntity,
  'ID' | 'Sender' | 'Subject' | 'MessageContentPreview' | 'GenerationStatus' |
  'ApprovalStatus' | 'ErrorType' | 'SentAt' | 'ForcedGeneration' |
  'ReceivedAt' | '__mj_UpdatedAt' | 'ApprovedReplyContent' | 'GeneratedReplyContent' |
  'ThreadID' | 'OutboundType' | 'Recipient'> & {
  AssignedToPersonID?: string | null;
  AssignedToContact?: string | null;
  /** True if this message was injected as a thread sibling and doesn't match the current filter */
  isThreadSibling?: boolean;
};

/**
 * A group of messages sharing the same ThreadID.
 */
interface ThreadGroup {
  type: 'thread';
  threadId: string;
  subject: string;
  participants: string[];
  messages: MessageListItem[];
  latestMessage: MessageListItem;
  latestTimestamp: Date;
  pendingCount: number;
  escalatedCount: number;
  /** Messages actually approvable RIGHT NOW (entity rule) — drives the thread bulk-approve
   *  button. pendingCount alone offered "Approve N pending" for still-generating drafts. */
  approvableCount: number;
  totalCount: number;
  isExpanded: boolean;
}

/**
 * A display item in the threaded message list.
 * Can be a thread group header, a standalone message, or an expanded thread child.
 */
interface DisplayItem {
  type: 'thread' | 'message' | 'group';
  thread?: ThreadGroup;
  message?: MessageListItem;
  isThreadChild?: boolean;
  parentThread?: ThreadGroup;
  /** Date-bucket label for type 'group' rows (Today / Yesterday / …) */
  groupLabel?: string;
}

/**
 * Message Approval Component
 *
 * A powerful human-in-the-loop review dashboard for AI-generated messages.
 * Features rapid triage, bulk operations, inline editing with diff view,
 * and keyboard shortcuts for efficient approval workflows.
 *
 * Keyboard Shortcuts:
 * - A: Approve selected message
 * - R: Reject selected message
 * - E: Toggle edit mode
 * - J/Down: Next message
 * - K/Up: Previous message
 * - Space: Toggle checkbox selection
 * - Escape: Cancel edit or clear selection
 */
@Component({
  standalone: false,
  selector: 'izzy-message-approval',
  templateUrl: './message-approval.component.html',
  styleUrls: ['./message-approval.component.css'],
  // Use None to allow styles to apply to dynamically injected HTML (htmldiff-js output)
  encapsulation: ViewEncapsulation.None,
  // One presence session per screen instance; Angular stops it when this component is destroyed.
  providers: [IzzyPresenceService]
})
@RegisterClass(BaseResourceComponent, 'IzzyMessageApprovalResource')
export class IzzyMessageApprovalResource extends BaseResourceComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input() initialState: MessageApprovalState | null = null;

  // ========== Organization/Channel Input Properties with Setters ==========
  private _organizationId: string | null = null;
  private _channelId: string | null = null;

  @Input()
  set organizationId(value: string | null) {
    const changed = this._organizationId !== value;
    this._organizationId = value;
    if (changed && !this.isLoading) {
      this.onOrganizationIdChange();
    }
  }
  get organizationId(): string | null {
    return this._organizationId;
  }

  @Input()
  set channelId(value: string | null) {
    const changed = this._channelId !== value;
    this._channelId = value;
    if (changed && !this.isLoading) {
      this.onChannelIdChange();
    }
  }
  get channelId(): string | null {
    return this._channelId;
  }
  @Output() stateChange = new EventEmitter<MessageApprovalState>();
  @Output() openRecord = new EventEmitter<{ EntityName: string; RecordPKey: CompositeKey }>();
  @Output() interaction = new EventEmitter<MessageApprovalInteraction>();

  async GetResourceDisplayName(data: ResourceData): Promise<string> {
    return 'Messages';
  }

  async GetResourceIconClass(data: ResourceData): Promise<string> {
    return 'fa-solid fa-envelope';
  }

  /** Live "who else is viewing this message" presence for the selected message. */
  protected readonly presence = inject(IzzyPresenceService);

  constructor(
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private location: Location,
    private authService: IzzyAuthService,
    private hostRef: ElementRef<HTMLElement>
  ) {
    super();
  }

  // ========== Organization & Channel Data ==========
  protected organizations: mjBizAppsCommonOrganizationEntity[] = [];
  protected channels: BCSaaSChannelEntity[] = [];
  protected filteredChannels: BCSaaSChannelEntity[] = [];
  protected selectedOrganization: mjBizAppsCommonOrganizationEntity | null = null;
  protected selectedChannel: BCSaaSChannelEntity | null = null;

  // ========== Message Data ==========
  protected messages: MessageListItem[] = [];
  protected selectedMessage: ChannelMessageEntityExtendedClient | null = null;
  /** Internal-note count per loaded message ID (0 recorded too, so a miss means "not fetched yet"). */
  protected noteCounts = new Map<string, number>();
  protected selectedIndex: number = -1;
  private entityCache: Map<string, ChannelMessageEntityExtendedClient> = new Map();

  // ========== Selection State ==========
  protected selectedIds: Set<string> = new Set();
  protected selectAllChecked: boolean = false;

  // ========== UI State ==========
  protected isLoading: boolean = true;
  protected isStatsLoading: boolean = true;
  protected isDetailLoading: boolean = false;
  protected isSaving: boolean = false;
  protected isReprocessing: boolean = false;
  /** Entry-time queue routing: consumed by the first loadStats() (see there for why). */
  private routeToEscalatedOnEntry = true;
  protected rescoringMessageIds: Set<string> = new Set();

  // Approval sends the reply immediately (server-side, inside Save()), so the 'A' shortcut arms
  // before it fires: the first press warns, a second press within ARM_WINDOW_MS commits. Guards
  // against a stray keystroke sending mail that cannot be recalled.
  protected approveArmed: boolean = false;
  private approveArmTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly ARM_WINDOW_MS = 4000;

  // Captured when the Force AI Generate dialog opens. The confirm handler runs later — possibly
  // after an in-flight save auto-advanced to another message — so it must act on the message the
  // user actually clicked, not on whatever selectedMessage points at by then.
  protected forceGenerateTarget: ChannelMessageEntityExtendedClient | null = null;

  protected get isRescoring(): boolean {
    if (!this.selectedMessage) return false;
    return this.rescoringMessageIds.has(this.selectedMessage.ID);
  }
  protected isWriteMode: boolean = false;
  protected errorMessage: string = '';
  protected successMessage: string = '';

  // ========== Developer Mode ==========
  protected isSystemDeveloper: boolean = false;
  protected isDevPanelCollapsed: boolean = true; // Default to collapsed

  // ========== Filter State ==========
  protected approvalStatusFilter: string = 'Pending';
  protected generationStatusFilter: string = 'Generated'; // v2.0: Renamed from statusFilter

  // ========== Filter Options (loaded from entity metadata) ==========
  protected approvalStatusOptions: string[] = [];
  protected generationStatusOptions: string[] = []; // v2.0: Renamed from statusOptions

  // ========== Stats ==========
  protected stats: MessageStats = {
    pending: 0,
    approved: 0,
    rejected: 0,
    notRequired: 0,
    escalate: 0,
    total: 0
  };

  // ========== Edit Mode ==========
  protected isEditMode: boolean = false;
  protected isEditorExpanded: boolean = false;
  protected editedContent: string = '';
  protected showDiff: boolean = false;
  protected diffHtml: SafeHtml = '';

  // ========== Reply View Mode (for reviewing edited messages) ==========
  protected replyViewMode: 'approved' | 'generated' | 'diff' = 'approved';

  // ========== Feedback ==========
  protected feedbackText: string = '';
  protected notificationMessage: string = '';

  // ========== Reply Recipients (CC / reply-all) ==========
  /** Outlook-style per-message mode; the ReplyAllDefault channel setting picks the initial position. */
  protected replyMode: 'reply' | 'replyAll' = 'reply';
  /** Editable comma-delimited CC list. Whatever is here at approve time is the approver's intent. */
  protected ccInput: string = '';
  /**
   * Per-message memory of the composer's Cc box + reply mode, keyed by message ID. The Cc lives
   * only in component state and initReplyRecipients() rebuilds the controls on EVERY select —
   * re-clicking the same message, switching queue filters, auto-advance — which silently blanked
   * a typed Cc (INTA 2026-09-03: the approver typed herself in, unassigned/reassigned, and
   * approved an empty box). Restored on select ahead of the defaults; dropped when the message
   * leaves the queue. Complemented by stampPlannedCC(), which persists the same intent on the
   * entity so it also survives a reload.
   */
  private ccMemory = new Map<string, { ccInput: string; replyMode: 'reply' | 'replyAll' }>();
  /** Map key for ccMemory — normalized like UUIDsEqual (SQL Server returns uppercase GUIDs, other paths lowercase). */
  private ccMemoryKey(id: string): string {
    return id.trim().toLowerCase();
  }
  /** True when the inbound message had recipients beyond the sender (reply-all is meaningful). */
  protected replyAllAvailable: boolean = false;
  /** The reply-all prefill computed for the selected message (cached for the mode toggle). */
  private replyAllPrefill: string[] = [];

  // ========== Outbound reply attachments ==========
  /** Staff-attached files that will go out with the approved reply (Direction='Outbound' rows). */
  protected replyAttachments: Array<{ ID: string; ArtifactID: string; Name: string; DisplayOrder: number }> = [];
  /** Slide-in preview drawer for staff-attached reply files before sending. */
  @ViewChild('replyAttachmentDrawer') replyAttachmentDrawer?: AttachmentDrawerComponent;
  /** Logged-in user — resolved via the MJ Metadata helper, non-null per MJ contract. */
  protected get currentUser(): UserInfo {
    return new Metadata().CurrentUser;
  }
  /** Default environment ID. Single environment in current deployment. */
  protected readonly environmentId: string = DEFAULT_ENVIRONMENT_ID;
  /** True while a file is uploading through the Attach Reply File action. */
  protected isUploadingAttachment: boolean = false;
  /** Last attach/remove failure, shown inline in the compose bar. */
  protected attachmentError: string = '';
  /** What the busy banner says while a decision is being written (approve = a real send in flight). */
  protected savingLabel: string = '';
  /** Which decision is being written ('Approved' | 'Rejected' | …) — drives the button transform. */
  protected savingAction: string = '';

  // ========== Auto-advance ==========
  protected autoAdvance: boolean = true;

  // ========== Spam Filter ==========
  protected hideSpam: boolean = true;

  // ========== Sort Mode ==========
  protected sortMode: 'received' | 'updated' = 'updated';

  // ========== Queue Filter ==========
  protected queueFilter: 'mine' | 'unassigned' | 'all' = 'all';

  // ========== Internal Notes Filter ==========
  protected notesFilter: NotesFilter = 'all';
  private notedMessageIds: string[] = [];
  protected readonly notesToggleOptions = [
    { key: 'all', label: 'All', title: 'All messages' },
    { key: 'with', label: 'With notes', title: 'Messages that have at least one internal note' },
    { key: 'without', label: 'Without notes', title: 'Messages with no internal notes' }
  ];

  // ========== Assignment ==========
  protected isAssignDropdownOpen: boolean = false;
  protected orgTeamMembers: { contactId: string; name: string }[] = [];
  protected isCurrentUserAdmin: boolean = false;
  protected assigneeFilterContactId: string | null = null;

  // ========== Infinite Scroll ==========
  protected readonly pageSize: number = 25;
  protected hasMore: boolean = false;
  protected isLoadingMore: boolean = false;
  /** Count of messages loaded from the main query (excludes injected thread siblings) */
  private queryLoadedCount: number = 0;

  // ========== Panel State ==========
  protected isPanelCollapsed: boolean = false;
  protected panelWidth: number = 340; // Default width in pixels

  // ========== Section Collapse State ==========
  protected isThreadContextExpanded: boolean = false;

  /** True when thread has messages with different approval statuses */
  get hasMultipleThreadStatuses(): boolean {
    if (this.threadContextMessages.length <= 1) return false;
    const statuses = new Set(this.threadContextMessages.map(m => m.ApprovalStatus));
    return statuses.size > 1;
  }

  /** Thread messages with a different status than the selected message (excludes current) */
  get otherStatusThreadMessages(): MessageListItem[] {
    if (!this.selectedMessage) return [];
    return this.threadContextMessages.filter(
      m => m.ID !== this.selectedMessage!.ID && m.ApprovalStatus !== this.selectedMessage!.ApprovalStatus
    );
  }

  /** Count of thread messages with a different status than the selected message */
  get otherStatusThreadCount(): number {
    return this.otherStatusThreadMessages.length;
  }

  // ========== Cross-Status Thread Indicators ==========
  /** Map of messageID -> related messages in other statuses */
  protected crossStatusThreadMap = new Map<string, MessageListItem[]>();
  /** Which message's thread popover is currently open */
  protected activeThreadPopoverId: string | null = null;

  /** Load cross-status thread data for all visible messages */
  private async loadCrossStatusThreadData(): Promise<void> {
    this.crossStatusThreadMap.clear();

    // Collect unique ThreadIDs from loaded messages
    const threadIds = [...new Set(this.messages.map(m => m.ThreadID).filter(Boolean))];
    if (threadIds.length === 0) return;

    try {
      const rv = new RunView();
      const result = await rv.RunView<MessageListItem>({
        EntityName: 'Channel Messages',
        ExtraFilter: `ThreadID IN (${threadIds.map(id => `'${id}'`).join(',')}) AND ChannelID = '${this.selectedChannel?.ID}'`,
        OrderBy: 'ReceivedAt ASC',
        ResultType: 'simple',
        MaxRows: 500, // safety bound, mirrors loadThreadSiblings
        Fields: ['Sender', 'Subject', 'ApprovalStatus', 'ReceivedAt', 'ThreadID']
      });

      if (result?.Success && result.Results) {
        // Group by ThreadID
        const threadGroups = new Map<string, MessageListItem[]>();
        for (const msg of result.Results) {
          if (!msg.ThreadID) continue;
          if (!threadGroups.has(msg.ThreadID)) {
            threadGroups.set(msg.ThreadID, []);
          }
          threadGroups.get(msg.ThreadID)!.push(msg);
        }

        // For each loaded message, check if its thread has messages with different statuses
        for (const msg of this.messages) {
          if (!msg.ThreadID) continue;
          const threadMembers = threadGroups.get(msg.ThreadID) || [];
          const otherStatusMembers = threadMembers.filter(
            m => m.ID !== msg.ID && m.ApprovalStatus !== msg.ApprovalStatus
          );
          if (otherStatusMembers.length > 0) {
            this.crossStatusThreadMap.set(msg.ID, otherStatusMembers);
          }
        }
      }
    } catch (error) {
      console.error('Error loading cross-status thread data:', error);
    }
  }

  /** Check if a message has related messages in other statuses */
  hasCrossStatusThread(messageId: string): boolean {
    return this.crossStatusThreadMap.has(messageId);
  }

  /** Get related messages for popover */
  getCrossStatusMessages(messageId: string): MessageListItem[] {
    return this.crossStatusThreadMap.get(messageId) || [];
  }

  /** Position for the thread popover */
  protected threadPopoverStyle: { [key: string]: string } = {};

  /** Toggle thread popover on a sidebar item */
  toggleThreadPopover(messageId: string, event: MouseEvent): void {
    event.stopPropagation();
    if (this.activeThreadPopoverId === messageId) {
      this.activeThreadPopoverId = null;
    } else {
      this.activeThreadPopoverId = messageId;
      // Position the popover near the clicked badge
      const target = event.target as HTMLElement;
      const rect = target.closest('.thread-indicator-badge')?.getBoundingClientRect();
      if (rect) {
        this.threadPopoverStyle = {
          top: (rect.bottom + 4) + 'px',
          left: Math.max(8, rect.left - 100) + 'px'
        };
      }
    }
  }
  protected isReplyCollapsed: boolean = false;
  protected isReasoningExpanded: boolean = false;

  // ========== Search State ==========
  protected searchQuery: string = '';
  protected filteredMessages: IzzyChannelMessageEntity[] = [];

  // ========== Thread State ==========
  protected expandedThreads: Set<string> = new Set();
  protected threadContextMessages: MessageListItem[] = [];
  protected isThreadContextLoading: boolean = false;
  private threadContextCache: Map<string, MessageListItem[]> = new Map();

  // ========== Dropdown State ==========

  /** Option models for the MJ components inside the filter popover */
  protected readonly sortOptions = [
    { value: 'updated', label: 'Updated' },
    { value: 'received', label: 'Received' }
  ];

  protected readonly queueToggleOptions = [
    { key: 'mine', label: 'Mine', title: 'Assigned to me' },
    { key: 'unassigned', label: 'Unassigned', title: 'Unassigned messages' },
    { key: 'all', label: 'All', title: 'All messages' }
  ];

  protected readonly assigneeDefaultItem = { contactId: '', name: 'Anyone' };

  onQueueToggle(key: string): void {
    this.setQueueFilter(key as 'mine' | 'unassigned' | 'all');
  }

  onNotesToggle(key: string): void {
    this.setNotesFilter(key as NotesFilter);
  }

  /** Active queue key for the toggle — none when filtering by a person */
  get queueToggleActiveKey(): string {
    return this.assigneeFilterContactId ? '' : this.queueFilter;
  }

  /** Non-default filters, for the mj-filter-popover badge */
  get activeFilterCount(): number {
    let n = 0;
    if (this.generationStatusFilter !== 'All') n++;
    if (this.queueFilter !== 'all') n++;
    if (this.assigneeFilterContactId) n++;
    if (this.notesFilter !== 'all') n++;
    return n;
  }

  clearAllFilters(): void {
    this.generationStatusFilter = 'All';
    this.notesFilter = 'all';
    this.setQueueFilter('all'); // clears assignee + reloads via its own path
    if (this.generationStatusFilter === 'All' && this.queueFilter === 'all') {
      this.loadData();
      this.loadStats();
    }
  }

  // ========== User Settings Persistence ==========
  private readonly userSettingsKey = 'Izzy.MessageApproval';
  private userSetting: MJUserSettingEntity | null = null;

  // ========== Confirm Dialog ==========
  protected confirmDialogVisible: boolean = false;
  protected confirmDialogTitle: string = '';
  protected confirmDialogMessage: string = '';
  protected confirmDialogAction: (() => Promise<void>) | null = null;
  protected confirmDialogButtonText: string = 'Yes';
  protected confirmDialogDanger: boolean = false;
  protected confirmDialogHasInput: boolean = false;
  protected confirmDialogInputValue: string = '';
  protected confirmDialogInputLabel: string = '';

  // ========== Cleanup ==========
  private userStateSubject = new BehaviorSubject<MessageApprovalState | null>(null);
  protected showStateUpdateNotification: boolean = false;

  // ========== Query Param Deep Linking ==========
  private _pendingMessageId: string | null = null;
  private _pendingChannelId: string | null = null;
  private _pendingOrganizationId: string | null = null;
  private _pendingApprovalStatus: string | null = null;
  private _pendingGenerationStatus: string | null = null;

  // ========== Swipe Gesture State ==========
  protected swipeState: { [key: string]: 'left' | 'right' | null } = {};
  protected swipingMessageId: string | null = null;
  private swipeStartX: number = 0;
  private swipeCurrentX: number = 0;
  private swipeThreshold: number = 80;  // Minimum swipe distance to trigger action
  private swipeMaxDistance: number = 120;  // Maximum visual swipe distance

  // ========== Entity Metadata ==========
  private channelMessageEntity: EntityInfo | null = null;

  /**
   * Current user state for persistence
   */
  private get currentUserState() {
    return {
      approvalStatusFilter: this.approvalStatusFilter,
      generationStatusFilter: this.generationStatusFilter, // v2.0: Renamed from statusFilter
      autoAdvance: this.autoAdvance,
      selectedMessageId: this.selectedMessage?.ID,
      isPanelCollapsed: this.isPanelCollapsed,
      panelWidth: this.panelWidth,
      selectedOrganizationId: this.selectedOrganization?.ID,
      selectedChannelId: this.selectedChannel?.ID,
      sortMode: this.sortMode,
      queueFilter: this.queueFilter,
      notesFilter: this.notesFilter
    };
  }

  // ========== Lifecycle ==========

  async ngOnInit(): Promise<void> {
    this.loadEntityMetadata();
    this.setupUserStateDebouncing();
    await this.loadUserState();

    // Check if user is a system developer (for showing developer info panel)
    this.isSystemDeveloper = await this.authService.isSystemDeveloper();

    // Check for navigation configuration from OpenNavItemByName (takes precedence)
    // This is how MJ passes parameters when navigating between resources
    this.handleNavigationConfiguration();

    // Subscribe to query param changes for deep linking and bookmarking
    // Only applies if no navigation configuration was set
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.handleQueryParams(params);
    });

    await this.loadOrganizationsAndChannels();
    await this.loadStats(); // Stats first — entry-time auto-route to Escalated happens here, before data loads
    await this.loadData();

    // Apply any pending selections from navigation config or query params after data is loaded
    await this.applyPendingSelections();

    this.NotifyLoadComplete();
  }

  ngOnDestroy(): void {
    if (this.approveArmTimer) {
      clearTimeout(this.approveArmTimer);
      this.approveArmTimer = null;
    }
    const currentState = this.currentUserState;
    if (currentState) {
      this.stateChange.emit(currentState);
    }
    super.ngOnDestroy();
  }

  /**
   * Handle organizationId setter change
   */
  private onOrganizationIdChange(): void {
    if (this._organizationId) {
      const org = this.organizations.find(o => o.ID === this._organizationId);
      if (org) {
        this.selectedOrganization = org;
        this.updateFilteredChannels();
        this.loadOrgTeamMembers();
        // Auto-select first channel for the new org
        if (this.filteredChannels.length > 0) {
          this.selectedChannel = this.filteredChannels[0];
        } else {
          this.selectedChannel = null;
        }
      }
    } else {
      this.selectedOrganization = null;
      this.selectedChannel = null;
      this.filteredChannels = [];
    }
    this.deselectMessage();
    this.refreshStatsAndData();
  }

  /**
   * Handle channelId setter change
   */
  private onChannelIdChange(): void {
    if (this._channelId) {
      const channel = this.filteredChannels.find(c => c.ID === this._channelId);
      if (channel) {
        this.selectedChannel = channel;
      }
    } else if (this.filteredChannels.length > 0) {
      // If no channel specified but we have channels, select first one
      this.selectedChannel = this.filteredChannels[0];
    } else {
      this.selectedChannel = null;
    }
    this.deselectMessage();
    this.refreshStatsAndData();
  }

  // ========== Keyboard Shortcuts ==========

  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcut(event: KeyboardEvent): void {
    // The workspace keeps this component mounted while other screens (e.g. admin)
    // are shown, and this listener is document-global — never handle shortcuts
    // while the component is not visible (offsetParent is null when hidden).
    if (!this.hostRef.nativeElement.offsetParent) {
      return;
    }

    // FIRST: Check if there's an active text selection - if so, don't interfere at all
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return;
    }

    // Only handle shortcuts when not in edit mode or text input. The compose dialog and the
    // attachment preview drawer block them entirely — focus can sit on the dialog/drawer chrome
    // (not an input), and 'A' or 'Esc' must never act on the message behind the overlay.
    if (this.isEditMode || this.showComposeDialog || this.replyAttachmentDrawer?.isDrawerOpen || this.isInputFocused(event)) {
      return;
    }

    // Don't handle if modifier keys are pressed (for copy/paste operations)
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    // A decision is already being written (and may auto-advance to another message when it lands),
    // so ignore shortcuts until it settles — same reason the action bar goes inert.
    if (this.isSaving || this.isReprocessing) {
      return;
    }

    switch (event.key.toLowerCase()) {
      case 'a':
        // Same guard as the Approve button's [disabled] — the status-only canApprove()
        // let 'A' arm-and-send messages with no reply content (which then send-failed)
        if (this.selectedMessage && this.canApproveMessage()) {
          event.preventDefault();
          if (this.approveArmed) {
            this.disarmApprove();
            this.approveMessage(this.selectedMessage);
          } else {
            this.armApprove();
          }
        }
        break;

      case 'r':
        if (this.selectedMessage && this.canRejectMessage()) {
          event.preventDefault();
          this.rejectMessage(this.selectedMessage);
        }
        break;

      case 'e':
        if (this.selectedMessage) {
          event.preventDefault();
          this.toggleEditMode();
        }
        break;

      case 'j':
      case 'arrowdown':
        event.preventDefault();
        this.selectNextMessage();
        break;

      case 'k':
      case 'arrowup':
        event.preventDefault();
        this.selectPreviousMessage();
        break;

      case ' ':
        if (this.selectedMessage) {
          event.preventDefault();
          this.toggleMessageSelection(this.selectedMessage);
        }
        break;

      case 'm':
        if (this.selectedMessage && !this.isEditMode) {
          event.preventDefault();
          this.assignToMe();
        }
        break;

      case '/':
        event.preventDefault();
        this.searchInputRef?.nativeElement.focus();
        break;

      case '?':
        event.preventDefault();
        this.showShortcutsOverlay = !this.showShortcutsOverlay;
        break;

      case 'enter':
        if (this.selectedMessage?.ThreadID) {
          event.preventDefault();
          const tid = this.selectedMessage.ThreadID;
          if (this.expandedThreads.has(tid)) {
            this.expandedThreads.delete(tid);
          } else {
            this.expandedThreads.add(tid);
          }
        }
        break;

      case 'escape':
        if (this.approveArmed) {
          this.disarmApprove();
        } else if (this.showShortcutsOverlay) {
          this.showShortcutsOverlay = false;
        } else if (this.isEditMode) {
          this.cancelEdit();
        } else {
          this.clearSelection();
        }
        break;
    }
  }

  /**
   * Arm the approve shortcut and warn. Approval sends the reply immediately and cannot be recalled,
   * so a single keystroke must never commit it.
   */
  private armApprove(): void {
    this.approveArmed = true;
    this.notificationMessage = 'Press A again to approve and send — this cannot be undone';
    this.showStateUpdateNotification = true;

    if (this.approveArmTimer) clearTimeout(this.approveArmTimer);
    this.approveArmTimer = setTimeout(
      () => this.disarmApprove(),
      IzzyMessageApprovalResource.ARM_WINDOW_MS
    );
  }

  /**
   * Clear the armed state. Called on commit, on Escape, on timeout, and whenever the selected
   * message changes — arming must never carry across to a different message.
   */
  private disarmApprove(): void {
    if (!this.approveArmed) return;
    this.approveArmed = false;
    this.notificationMessage = '';
    this.showStateUpdateNotification = false;

    if (this.approveArmTimer) {
      clearTimeout(this.approveArmTimer);
      this.approveArmTimer = null;
    }
  }

  private isInputFocused(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement;

    // Check for form inputs
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
      return true;
    }

    // Rich-text surfaces (Quill, FAQ editors, etc.) are contenteditable DIVs,
    // not form controls — typing there must never trigger shortcuts.
    if (target.isContentEditable) {
      return true;
    }

    // Check if target is within email body content (where users might be selecting text)
    // This prevents keyboard shortcuts from interfering with text selection
    const emailBody = target.closest('.email-body, .markdown-content');
    if (emailBody) {
      return true;
    }

    // Check if there's an active text selection
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return true;
    }

    return false;
  }

  // ========== Initialization ==========

  private loadEntityMetadata(): void {
    const md = new Metadata();
    this.channelMessageEntity = md.EntityByName('Channel Messages');

    if (this.channelMessageEntity) {
      // Load ApprovalStatus options from entity field values
      const approvalStatusField = this.channelMessageEntity.Fields.find(f => f.Name === 'ApprovalStatus');
      if (approvalStatusField?.ValueListType === 'List' && approvalStatusField.EntityFieldValues) {
        this.approvalStatusOptions = ['All', ...approvalStatusField.EntityFieldValues.map((v: EntityFieldValueInfo) => v.Value)];
      } else {
        this.approvalStatusOptions = ['All', 'Pending', 'Approved', 'Rejected', 'Not Required'];
      }

      // Load GenerationStatus options from entity field values (v2.0: renamed from Status)
      const generationStatusField = this.channelMessageEntity.Fields.find(f => f.Name === 'GenerationStatus');
      if (generationStatusField?.ValueListType === 'List' && generationStatusField.EntityFieldValues) {
        this.generationStatusOptions = ['All', ...generationStatusField.EntityFieldValues.map((v: EntityFieldValueInfo) => v.Value)];
      } else {
        this.generationStatusOptions = ['All', 'Read', 'Creating Reply', 'Generated', 'Skipped', 'Failed'];
      }
    }
  }

  private async loadUserState(): Promise<void> {
    // First load from MJUserSettingEntity
    await this.loadUserSettings();

    // Then overlay with any state from initialState (parent component can override)
    const state = this.initialState;
    if (state) {
      if (state.approvalStatusFilter) this.approvalStatusFilter = state.approvalStatusFilter;
      if (state.generationStatusFilter) this.generationStatusFilter = state.generationStatusFilter; // v2.0
      if (state.autoAdvance !== undefined) this.autoAdvance = state.autoAdvance;
      if (state.isPanelCollapsed !== undefined) this.isPanelCollapsed = state.isPanelCollapsed;
      if (state.panelWidth) this.panelWidth = state.panelWidth;
      if (state.sortMode) this.sortMode = state.sortMode;
      if (state.queueFilter) this.queueFilter = state.queueFilter;
      if (state.notesFilter) this.notesFilter = state.notesFilter;
      // Note: selectedOrganizationId and selectedChannelId are passed via inputs from parent
    }
  }

  // ========== Navigation Configuration & Deep Linking ==========

  /**
   * Handle navigation configuration passed via OpenNavItemByName
   * This takes precedence over URL query params for in-app navigation
   * Configuration is passed through this.Data.Configuration by MJ's navigation system
   */
  private handleNavigationConfiguration(): void {
    const config = this.Data?.Configuration;
    if (!config) return;

    // Check for navigation params in the configuration
    // These are set by Analytics.navigateToMessage() via NavigationService.OpenNavItemByName()
    if (config.messageId) {
      this._pendingMessageId = config.messageId as string;
    }
    if (config.channelId) {
      this._pendingChannelId = config.channelId as string;
    }
    if (config.organizationId) {
      this._pendingOrganizationId = config.organizationId as string;
    }
    if (config.approvalStatus) {
      this._pendingApprovalStatus = config.approvalStatus as string;
    }
    if (config.generationStatus) {
      this._pendingGenerationStatus = config.generationStatus as string;
    }
  }

  /**
   * Handle query params for deep linking from browser URL
   * Supports bookmarking and direct URL access
   * Only sets pending values if not already set by navigation configuration
   */
  private handleQueryParams(params: Record<string, string>): void {
    // Store pending selections - only if not already set by navigation configuration
    // Navigation configuration takes precedence over URL query params
    if (params['messageId'] && !this._pendingMessageId) {
      this._pendingMessageId = params['messageId'];
    }
    if (params['channelId'] && !this._pendingChannelId) {
      this._pendingChannelId = params['channelId'];
    }
    if (params['organizationId'] && !this._pendingOrganizationId) {
      this._pendingOrganizationId = params['organizationId'];
    }
    if (params['approvalStatus'] && !this._pendingApprovalStatus) {
      this._pendingApprovalStatus = params['approvalStatus'];
    }
    if (params['generationStatus'] && !this._pendingGenerationStatus) {
      this._pendingGenerationStatus = params['generationStatus'];
    }

    // If data is already loaded, apply immediately
    if (!this.isLoading && this.messages.length > 0) {
      this.applyPendingSelections();
    }
  }

  /**
   * Apply pending selections from query params after data loads
   */
  private async applyPendingSelections(): Promise<void> {
    let needsReload = false;

    // Apply organization selection first
    if (this._pendingOrganizationId) {
      const org = this.organizations.find(o => o.ID === this._pendingOrganizationId);
      if (org && org.ID !== this.selectedOrganization?.ID) {
        this.selectedOrganization = org;
        this.updateFilteredChannels();
        needsReload = true;
      }
      this._pendingOrganizationId = null;
    }

    // Apply channel selection
    if (this._pendingChannelId) {
      const channel = this.filteredChannels.find(c => c.ID === this._pendingChannelId);
      if (channel && channel.ID !== this.selectedChannel?.ID) {
        this.selectedChannel = channel;
        needsReload = true;
      }
      this._pendingChannelId = null;
    }

    // Apply filter selections - set to the message's status to ensure it appears
    if (this._pendingApprovalStatus && this._pendingApprovalStatus !== this.approvalStatusFilter) {
      this.approvalStatusFilter = this._pendingApprovalStatus;
      needsReload = true;
      this._pendingApprovalStatus = null;
    }

    if (this._pendingGenerationStatus && this._pendingGenerationStatus !== this.generationStatusFilter) {
      this.generationStatusFilter = this._pendingGenerationStatus;
      needsReload = true;
      this._pendingGenerationStatus = null;
    }

    // If we changed filters or have a pending message, reload data
    if (needsReload || this._pendingMessageId) {
      await this.refreshStatsAndData();
    }

    // Try to select the specific message
    await this.selectPendingMessage();
  }

  /**
   * Select a pending message from query params
   */
  private async selectPendingMessage(): Promise<void> {
    if (this._pendingMessageId) {
      const message = this.messages.find(m => m.ID === this._pendingMessageId);
      if (message) {
        const index = this.messages.indexOf(message);
        await this.selectMessage(message, index);
      }
      this._pendingMessageId = null;
      // Clear the query params from URL
      this.clearQueryParams();
    }
  }

  /**
   * Clear query params from URL without navigating
   */
  private clearQueryParams(): void {
    const path = this.location.path().split('?')[0];
    this.location.replaceState(path);
  }

  // ========== Organization & Channel Loading ==========

  private async loadOrganizationsAndChannels(): Promise<void> {
    try {
      // Initialize IzzyMetadataEngine (does nothing if already initialized)
      await IzzyMetadataEngine.Instance.Config(false);

      // Load from cached data
      this.organizations = IzzyMetadataEngine.Instance.GetCurrentUserOrganizations();
      this.channels = IzzyMetadataEngine.Instance.Channels;

      // Org/Channel are now managed by parent container (messages-container)
      // Apply organizationId input if provided
      if (this.organizationId) {
        const org = this.organizations.find(o => o.ID === this.organizationId);
        if (org) {
          this.selectedOrganization = org;
          this.updateFilteredChannels();

          // Use channelId input if provided
          if (this.channelId) {
            const channel = this.filteredChannels.find(c => c.ID === this.channelId);
            if (channel) {
              this.selectedChannel = channel;
            }
          } else if (this.filteredChannels.length > 0) {
            this.selectedChannel = this.filteredChannels[0];
          }
          this.loadOrgTeamMembers();
          return;
        }
      }

      // Fallback: Restore selection from initialState (standalone mode only)
      const state = this.initialState;
      if (state?.selectedOrganizationId) {
        const org = this.organizations.find(o => o.ID === state.selectedOrganizationId);
        if (org) {
          this.selectedOrganization = org;
          this.updateFilteredChannels();

          if (state.selectedChannelId) {
            const channel = this.filteredChannels.find(c => c.ID === state.selectedChannelId);
            if (channel) {
              this.selectedChannel = channel;
            }
          }
        }
      }

      // If no selection yet, auto-select first org and channel
      if (!this.selectedOrganization && this.organizations.length > 0) {
        this.selectedOrganization = this.organizations[0];
        this.updateFilteredChannels();
        if (this.filteredChannels.length > 0) {
          this.selectedChannel = this.filteredChannels[0];
        }
      }

      // Load team members for assignment dropdown
      this.loadOrgTeamMembers();
    } catch (error) {
      console.error('Error loading organizations and channels:', error);
    }
  }

  private updateFilteredChannels(): void {
    if (!this.selectedOrganization) {
      this.filteredChannels = [];
      return;
    }

    // Filter channels by selected organization
    this.filteredChannels = this.channels.filter(
      c => c.OrganizationID === this.selectedOrganization!.ID
    );
  }

  onOrganizationChange(orgId: string): void {
    const org = this.organizations.find(o => o.ID === orgId);
    if (org) {
      this.selectedOrganization = org;
      this.updateFilteredChannels();
      this.loadOrgTeamMembers();

      // Auto-select first channel for the new org
      if (this.filteredChannels.length > 0) {
        this.selectedChannel = this.filteredChannels[0];
      } else {
        this.selectedChannel = null;
      }

      this.deselectMessage();
      this.saveUserSettings();
      this.updateUserState();
      this.refreshStatsAndData();
    }
  }

  onChannelChange(channelId: string): void {
    const channel = this.filteredChannels.find(c => c.ID === channelId);
    if (channel) {
      this.selectedChannel = channel;
      this.deselectMessage();
      this.saveUserSettings();
      this.updateUserState();
      this.refreshStatsAndData();
    }
  }

  private setupUserStateDebouncing(): void {
    this.userStateSubject.pipe(
      debounceTime(2000),
      takeUntil(this.destroy$)
    ).subscribe(state => {
      if (state) {
        this.stateChange.emit(state);
        // Silently save state without showing notification (was distracting)
      }
    });
  }

  private updateUserState(): void {
    this.userStateSubject.next(this.currentUserState);
  }

  // ========== Data Loading ==========

  async Refresh(): Promise<void> {
    await this.refreshStatsAndData();
  }

  private async refreshStatsAndData(): Promise<void> {
    await this.loadStats();  // Stats first, then data with the current filter (no auto-routing after entry)
    await this.loadData();   // Then load data with the correct filter
  }

  async loadData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';
    this.entityCache.clear();
    this.threadContextCache.clear();

    try {
      if (!this.selectedChannel) {
        this.messages = [];
        this.isLoading = false;
        return;
      }

      if (this.notesFilter !== 'all') {
        await this.loadNotedMessageIds();
      }

      this.messages = [];
      this.hasMore = false;
      this.queryLoadedCount = 0;

      const extraFilter = this.buildExtraFilter();
      const search = this.currentSearch();
      const orderByField = this.sortMode === 'received' ? 'ReceivedAt' : '__mj_UpdatedAt';

      const rv = new RunView();
      const result = await rv.RunView<MessageListItem>({
        EntityName: 'Channel Messages',
        OrderBy: `${orderByField} DESC`,
        ExtraFilter: extraFilter,
        // Free text rides on MJ's own search route (full-text index when live); operators stay in ExtraFilter.
        UserSearchString: search.userSearchString,
        ResultType: 'simple',
        Fields: ['Sender', 'Subject', 'Recipient', 'MessageContentPreview', 'GenerationStatus',
                 'ApprovalStatus', 'ErrorType', 'SentAt', 'ForcedGeneration',
                 'ReceivedAt', '__mj_UpdatedAt', 'ApprovedReplyContent', 'GeneratedReplyContent',
                 'AssignedToPersonID', 'ThreadID', 'OutboundType'],
        MaxRows: this.pageSize + 1,
        StartRow: 0
      });

      if (result?.Success && result.Results) {
        if (result.Results.length > this.pageSize) {
          this.hasMore = true;
          this.messages = result.Results.slice(0, this.pageSize);
          this.queryLoadedCount = this.pageSize;
        } else {
          this.hasMore = false;
          this.messages = result.Results;
          this.queryLoadedCount = result.Results.length;
        }
      } else {
        // RunView reports failure through the result, not by throwing; a failed search must read as one, not as "no messages".
        // The provider's message carries the generated SQL (view, columns, filter); that goes to the console, not the banner.
        console.error('[message-approval] load failed:', result?.ErrorMessage ?? 'unknown error');
        this.errorMessage = this.isSearchActive ? 'Search failed. Check the search text or try again.' : 'Failed to load messages.';
      }

      // Note-count badges for this page (non-blocking; a failure just leaves badges off).
      void this.loadNoteCounts(true);

      // Clear selection if selected message no longer in list
      if (this.selectedMessage && !this.messages.find(m => m.ID === this.selectedMessage!.ID)) {
        this.deselectMessage();
      }

      // Auto-select first message if none selected and messages exist
      if (!this.selectedMessage && this.messages.length > 0) {
        this.selectMessage(this.messages[0], 0);
      }

      // Load cross-status thread indicators (non-blocking)
      await this.loadThreadSiblings();
    } catch (error) {
      console.error('Error loading messages:', error);
      this.errorMessage = 'Failed to load messages. Please try again.';
      this.messages = [];
    } finally {
      this.isLoading = false;
    }
  }

  // ========== Infinite Scroll Methods ==========

  private buildExtraFilter(): string {
    const filters: string[] = [];
    filters.push(`ChannelID = '${this.selectedChannel!.ID}'`);

    const isSearching = this.searchQuery.trim().length > 0;

    if (!isSearching) {
      if (this.approvalStatusFilter === 'Rejected') {
        filters.push(`ApprovalStatus IN ('Rejected', 'Not Required')`);
      } else if (this.approvalStatusFilter !== 'All') {
        filters.push(`ApprovalStatus = '${this.approvalStatusFilter}'`);
      }
      if (this.generationStatusFilter !== 'All') {
        filters.push(`GenerationStatus = '${this.generationStatusFilter}'`);
      }
    }

    if (isSearching) {
      filters.push(this.buildSearchClause(this.searchQuery));
    }

    if (this.hideSpam) {
      filters.push(`IsSpam = 0`);
    }

    if (this.assigneeFilterContactId) {
      filters.push(`AssignedToPersonID = '${this.assigneeFilterContactId}'`);
    } else if (this.queueFilter === 'mine') {
      const myContactId = IzzyMetadataEngine.Instance.CurrentContactID;
      if (myContactId) {
        filters.push(`AssignedToPersonID = '${myContactId}'`);
      }
    } else if (this.queueFilter === 'unassigned') {
      filters.push(`AssignedToPersonID IS NULL`);
    }

    const notesClause = this.notesFilterClause();
    if (notesClause) {
      filters.push(notesClause);
    }

    return filters.join(' AND ');
  }

  /**
   * Resolves the distinct ChannelMessageIDs that have internal notes via RunView so
   * ClientClauseScreen.AssertUnscopedRead does not reject a subquery on [Izzy].[vwChannelMessageNotes].
   */
  private async loadNotedMessageIds(): Promise<void> {
    try {
      const orgId = this.selectedChannel?.OrganizationID ?? this.selectedOrganization?.ID;
      const rv = new RunView();
      const res = await rv.RunView<Pick<IzzyChannelMessageNoteEntity, 'ChannelMessageID'>>({
        EntityName: 'Channel Message Notes',
        ExtraFilter: orgId ? `OrganizationID = '${orgId.replace(/'/g, "''")}'` : undefined,
        Fields: ['ChannelMessageID'],
        ResultType: 'simple',
      });
      if (res.Success && res.Results) {
        this.notedMessageIds = Array.from(
          new Set(res.Results.map(r => r.ChannelMessageID).filter((id): id is string => !!id))
        );
      } else {
        this.notedMessageIds = [];
      }
    } catch {
      this.notedMessageIds = [];
    }
  }

  /**
   * SQL for the internal-notes filter (null when 'all'). Shared by the list query and the status pill counts.
   * NOTE: Follow-up at high note volume — replace literal ID IN (...) list with a server-side
   * NoteCount column on vwChannelMessages or an RLS-safe table-valued function.
   */
  private notesFilterClause(): string | null {
    if (this.notesFilter === 'all') return null;
    if (this.notedMessageIds.length === 0) {
      return this.notesFilter === 'with' ? '1 = 0' : null;
    }
    const idList = this.notedMessageIds.map(id => `'${id.replace(/'/g, "''")}'`).join(', ');
    if (this.notesFilter === 'with') return `ID IN (${idList})`;
    if (this.notesFilter === 'without') return `ID NOT IN (${idList})`;
    return null;
  }

  /**
   * Whether Channel Messages has a live full-text index: the free text then goes out as
   * `UserSearchString` and MJ routes it through the generated search function. Read from the
   * entity metadata the browser loaded at sign-in; without the flag (or after a rollback) the
   * shared search builder puts a LIKE on the plain-text column into the filter instead.
   */
  private get fullTextAvailable(): boolean {
    const entity = new Metadata().EntityByName('Channel Messages');
    return !!entity?.FullTextSearchEnabled && !!entity?.FullTextSearchFunction;
  }

  /**
   * The parsed and shaped search box value. All shaping (operators, stop words, prefix terms,
   * phrases, the 255-character cap, escaping, the fallback filter) lives in the shared
   * `BuildChannelMessageSearch` so every caller of the index behaves like this screen.
   */
  private currentSearch(): ChannelMessageSearch {
    return BuildChannelMessageSearch(this.searchQuery, { fullTextAvailable: this.fullTextAvailable });
  }

  /** The search's contribution to `ExtraFilter`: operator clauses (and the fallback body clause when full-text is not live). */
  private buildSearchClause(rawQuery: string): string {
    const search = BuildChannelMessageSearch(rawQuery, { fullTextAvailable: this.fullTextAvailable });
    return search.extraFilter.length > 0 ? search.extraFilter : `1=1`;
  }

  private operatorMatches(value: string, operator: string | undefined): boolean {
    return !!operator && value.toLowerCase().includes(operator.toLowerCase());
  }

  /**
   * Contextual status chips: a row's approval chip renders only when it adds
   * information — always for Escalated, otherwise only in All/search views
   * (in the Pending view, "Pending" on every row is noise).
   */
  showApprovalChip(m: MessageListItem): boolean {
    return m.ApprovalStatus === 'Escalated'
      || this.approvalStatusFilter === 'All'
      || this.isSearchActive;
  }

  /** True while the list is showing server-side search results. */
  get isSearchActive(): boolean {
    return this.searchQuery.trim().length > 0;
  }

  /**
   * Which fields matched for a row, for the match-type chips. Body match is
   * inferred: the server returned the row but no visible field contains the
   * term (we don't ship full MessageContent to the list).
   */
  getMatchTypes(message: MessageListItem): Array<'sender' | 'subject' | 'recipient' | 'body'> {
    if (!this.isSearchActive) return [];
    const search = this.currentSearch();
    const hasFreeText = search.words.length > 0 || search.phrase !== null;
    const types: Array<'sender' | 'subject' | 'recipient' | 'body'> = [];

    const sender = message.Sender ?? '';
    const subject = message.Subject ?? '';
    const recipient = (message as { Recipient?: string }).Recipient ?? '';

    // Same model as the index: every word as a prefix (any order), or the exact phrase; operators are substrings.
    if (this.operatorMatches(sender, search.operators.from) || ChannelMessageSearchMatches(sender, search)) types.push('sender');
    if (this.operatorMatches(subject, search.operators.subject) || ChannelMessageSearchMatches(subject, search)) types.push('subject');
    if (this.operatorMatches(recipient, search.operators.to) || ChannelMessageSearchMatches(recipient, search)) types.push('recipient');
    // Body is inferred: the server returned the row but no visible field holds the search. Only the
    // displayed slice of the preview counts as visible (the stored preview is up to 500 chars, the list
    // shows 100): a hit in the hidden tail must still be explained by the chip.
    const visiblePreview = (message.MessageContentPreview ?? '').substring(0, 100);
    if (hasFreeText && types.length === 0 && !ChannelMessageSearchMatches(visiblePreview, search)) types.push('body');

    return types;
  }

  async loadMoreMessages(): Promise<void> {
    if (this.isLoadingMore || !this.hasMore || this.isLoading) return;

    this.isLoadingMore = true;
    try {
      const extraFilter = this.buildExtraFilter();
      const search = this.currentSearch();
      const orderByField = this.sortMode === 'received' ? 'ReceivedAt' : '__mj_UpdatedAt';
      const startRow = this.queryLoadedCount;

      const rv = new RunView();
      const result = await rv.RunView<MessageListItem>({
        EntityName: 'Channel Messages',
        OrderBy: `${orderByField} DESC`,
        ExtraFilter: extraFilter,
        UserSearchString: search.userSearchString,
        ResultType: 'simple',
        Fields: ['Sender', 'Subject', 'Recipient', 'MessageContentPreview', 'GenerationStatus',
                 'ApprovalStatus', 'ErrorType', 'SentAt', 'ForcedGeneration',
                 'ReceivedAt', '__mj_UpdatedAt', 'ApprovedReplyContent', 'GeneratedReplyContent',
                 'AssignedToPersonID', 'ThreadID', 'OutboundType'],
        MaxRows: this.pageSize + 1,
        StartRow: startRow
      });

      if (result?.Success && result.Results) {
        if (result.Results.length > this.pageSize) {
          this.hasMore = true;
          this.messages = [...this.messages, ...result.Results.slice(0, this.pageSize)];
          this.queryLoadedCount += this.pageSize;
        } else {
          this.hasMore = false;
          this.messages = [...this.messages, ...result.Results];
          this.queryLoadedCount += result.Results.length;
        }
      } else {
        this.hasMore = false;
        console.error('[message-approval] load-more failed:', result?.ErrorMessage ?? 'unknown error');
        this.errorMessage = this.isSearchActive ? 'Search failed. Check the search text or try again.' : 'Failed to load more messages.';
      }
      void this.loadNoteCounts();

      await this.loadThreadSiblings();
    } catch (error) {
      console.error('Error loading more messages:', error);
    } finally {
      this.isLoadingMore = false;
    }
  }

  /**
   * Fetch and inject missing thread siblings that don't match the current filter.
   * This ensures all thread members appear in the sidebar grouping regardless of status.
   */
  /**
   * Fetches internal-note counts for loaded messages that don't have one yet (one query per page,
   * ChannelMessageID only, counted client-side). `replace` resets the map on a fresh first page.
   */
  private async loadNoteCounts(replace: boolean = false): Promise<void> {
    if (replace) this.noteCounts.clear();
    const ids = this.messages.map(m => m.ID).filter(id => !this.noteCounts.has(id));
    if (ids.length === 0) return;
    try {
      const rv = new RunView();
      const res = await rv.RunView<Pick<IzzyChannelMessageNoteEntity, 'ChannelMessageID'>>({
        EntityName: 'Channel Message Notes',
        ExtraFilter: `ChannelMessageID IN (${ids.map(id => `'${id}'`).join(', ')})`,
        Fields: ['ChannelMessageID'],
        ResultType: 'simple',
      });
      if (!res.Success) return;
      const counts = new Map<string, number>();
      for (const row of res.Results ?? []) {
        counts.set(row.ChannelMessageID, (counts.get(row.ChannelMessageID) ?? 0) + 1);
      }
      for (const id of ids) {
        this.noteCounts.set(id, counts.get(id) ?? 0);
      }
    } catch (error) {
      console.error('Error loading internal-note counts:', error);
    }
  }

  /** Viewer saved a note on the open message — update that card's badge without a reload. */
  protected onNoteAdded(event: { messageId: string; noteCount: number }): void {
    this.noteCounts.set(event.messageId, event.noteCount);
  }

  private async loadThreadSiblings(): Promise<void> {
    if (!this.selectedChannel) return;

    // Collect ThreadIDs from loaded messages
    const threadIds = [...new Set(this.messages.map(m => m.ThreadID).filter(Boolean))];
    if (threadIds.length === 0) return;

    // IDs already loaded
    const loadedIds = new Set(this.messages.map(m => m.ID));

    try {
      const rv = new RunView();
      const result = await rv.RunView<MessageListItem>({
        EntityName: 'Channel Messages',
        ExtraFilter: `ThreadID IN (${threadIds.map(id => `'${id}'`).join(',')}) AND ChannelID = '${this.selectedChannel.ID}'`,
        OrderBy: 'ReceivedAt ASC',
        ResultType: 'simple',
        MaxRows: 500, // safety bound — threadIds is capped by page size, but a pathological thread shouldn't flood the list
        Fields: ['Sender', 'Subject', 'Recipient', 'MessageContentPreview', 'GenerationStatus',
                 'ApprovalStatus', 'ErrorType', 'SentAt', 'ForcedGeneration',
                 'ReceivedAt', '__mj_UpdatedAt', 'ApprovedReplyContent', 'GeneratedReplyContent',
                 'AssignedToPersonID', 'ThreadID', 'OutboundType']
      });

      if (result?.Success && result.Results) {
        const siblings = result.Results.filter(m => !loadedIds.has(m.ID));
        if (siblings.length > 0) {
          this.messages = [...this.messages, ...siblings];
        }
      }
      void this.loadNoteCounts();

      // Set isThreadSibling flag based on current filter for ALL messages
      if (this.approvalStatusFilter !== 'All') {
        for (const msg of this.messages) {
          if (this.approvalStatusFilter === 'Rejected') {
            msg.isThreadSibling = !['Rejected', 'Not Required'].includes(msg.ApprovalStatus);
          } else {
            msg.isThreadSibling = msg.ApprovalStatus !== this.approvalStatusFilter;
          }
        }
      } else {
        for (const msg of this.messages) {
          msg.isThreadSibling = false;
        }
      }
    } catch (error) {
      console.error('Error loading thread siblings:', error);
    }
  }

  ngAfterViewInit(): void {
    // Scroll listener is set up via template (scroll) binding
  }

  /** Called from template (scroll) event on .message-items-container */
  onMessageListScroll(event: Event): void {
    if (!this.hasMore || this.isLoadingMore || this.isLoading) return;

    const el = event.target as HTMLElement;
    const threshold = 200;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

    if (distanceFromBottom < threshold) {
      this.loadMoreMessages();
    }
  }

  private async loadStats(): Promise<void> {
    this.isStatsLoading = true;
    try {
      // If no channel selected, show zero stats
      if (!this.selectedChannel) {
        this.stats = { pending: 0, approved: 0, rejected: 0, notRequired: 0, escalate: 0, total: 0 };
        return;
      }

      if (this.notesFilter !== 'all') {
        await this.loadNotedMessageIds();
      }

      // Build base filter matching the queue filter context
      const baseFilters: string[] = [];
      baseFilters.push(`ChannelID = '${this.selectedChannel.ID}'`);

      if (this.generationStatusFilter !== 'All') {
        baseFilters.push(`GenerationStatus = '${this.generationStatusFilter}'`);
      }

      // Apply queue assignment filter so pill counts reflect the selected queue
      if (this.assigneeFilterContactId) {
        baseFilters.push(`AssignedToPersonID = '${this.assigneeFilterContactId}'`);
      } else if (this.queueFilter === 'mine') {
        const myContactId = IzzyMetadataEngine.Instance.CurrentContactID;
        if (myContactId) {
          baseFilters.push(`AssignedToPersonID = '${myContactId}'`);
        }
      } else if (this.queueFilter === 'unassigned') {
        baseFilters.push(`AssignedToPersonID IS NULL`);
      }

      const notesClause = this.notesFilterClause();
      if (notesClause) {
        baseFilters.push(notesClause);
      }

      const baseFilter = baseFilters.join(' AND ');

      // One batched round-trip of count-only queries per status. The previous
      // implementation streamed EVERY message's ApprovalStatus over the wire
      // and counted client-side, which scaled with channel size.
      const statuses: Array<{ status: string; key: 'pending' | 'approved' | 'rejected' | 'notRequired' | 'escalate' }> = [
        { status: 'Pending', key: 'pending' },
        { status: 'Approved', key: 'approved' },
        { status: 'Rejected', key: 'rejected' },
        { status: 'Not Required', key: 'notRequired' },
        { status: 'Escalated', key: 'escalate' }
      ];

      const rv = new RunView();
      const results = await rv.RunViews(statuses.map(s => ({
        EntityName: 'Channel Messages',
        ExtraFilter: `${baseFilter} AND ApprovalStatus = '${s.status}'`,
        ResultType: 'count_only' as const
      })));

      const counts: Record<string, number> = {
        pending: 0, approved: 0, rejected: 0, notRequired: 0, escalate: 0
      };
      let total = 0;

      statuses.forEach((s, i) => {
        const count = results?.[i]?.Success ? (results[i].TotalRowCount ?? 0) : 0;
        counts[s.key] = count;
        total += count;
      });

      this.stats = {
        pending: counts['pending'],
        approved: counts['approved'],
        rejected: counts['rejected'],
        notRequired: counts['notRequired'],
        escalate: counts['escalate'],
        total: total
      };

      // Auto-select Escalated on ENTRY only (highest priority queue). This used to run on every
      // stats refresh — so approving/rejecting/editing a Pending message (saveAndAdvance reloads
      // stats) yanked the agent to Escalated mid-triage. The rule is a landing decision, not a
      // standing one: route once when the screen opens; after that the agent owns the filter.
      if (this.routeToEscalatedOnEntry && this.stats.escalate > 0 && this.approvalStatusFilter === 'Pending') {
        this.approvalStatusFilter = 'Escalated';
      }
      this.routeToEscalatedOnEntry = false;
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      this.isStatsLoading = false;
    }
  }

  // ========== Message Selection ==========

  async selectMessage(listItem: MessageListItem, index: number): Promise<void> {
    // Moving to another message cancels any armed approval — the arm belongs to the message it
    // was raised on, and auto-advance lands here too.
    this.disarmApprove();
    this.replyAttachmentDrawer?.close();
    this.selectedIndex = index;
    this.isDetailLoading = true;
    this.isEditMode = false;
    this.isReasoningExpanded = false;
    this.showDiff = false;
    this.diffHtml = '';

    try {
      const entity = await this.loadMessageEntity(listItem.ID);
      if (!entity) {
        this.selectedMessage = null;
        this.errorMessage = 'Failed to load message details.';
        return;
      }

      this.selectedMessage = entity;
      this.editedContent = entity.GeneratedReplyContent || '';
      this.feedbackText = entity.ApprovalFeedback || '';
      this.initReplyRecipients(entity);
      // Load staff-attached outbound files for the compose bar (non-blocking, like thread context)
      this.loadReplyAttachments(entity);

      this.replyViewMode = 'approved';
      if (entity.ApprovedReplyContent) {
        this.computeHtmlDiff(entity.GeneratedReplyContent || '', entity.ApprovedReplyContent || '');
      }

      // Load thread context (non-blocking — don't await)
      this.loadThreadContext(entity);

      this.updateUserState();
      this.interaction.emit({
        type: 'selectMessage',
        messageId: entity.ID
      });
    } catch (error) {
      console.error('Error loading message details:', error);
      this.selectedMessage = null;
      this.errorMessage = 'Failed to load message details.';
    } finally {
      this.isDetailLoading = false;
      this.syncMessagePresence();
    }
  }

  /** Point presence at whatever message is now open in the detail pane (or stop when none is). */
  private syncMessagePresence(): void {
    this.presence.watch(this.selectedMessage?.ID ?? null, this.organizationId);
  }

  // ========== Outbound reply attachments ==========

  /**
   * Loads the staff-attached outbound files for the selected message's compose bar. The join
   * entity's `Artifact` name-join column carries the filename (Artifact.Name = the original
   * file name), so one query paints the chips.
   */
  private async loadReplyAttachments(message: IzzyChannelMessageEntity): Promise<void> {
    this.replyAttachments = [];
    this.attachmentError = '';
    if (message.ApprovalStatus !== 'Pending' && message.ApprovalStatus !== 'Escalated') {
      return;
    }
    const rv = new RunView();
    const result = await rv.RunView<{ ID: string; ArtifactID: string; Artifact: string; DisplayOrder: number }>({
      EntityName: 'Channel Message Attachments',
      ExtraFilter: `ChannelMessageID = '${message.ID}' AND Direction = 'Outbound'`,
      Fields: ['ID', 'ArtifactID', 'Artifact', 'DisplayOrder'],
      OrderBy: 'DisplayOrder ASC',
      ResultType: 'simple'
    });
    if (result?.Success && this.selectedMessage?.ID === message.ID) {
      this.replyAttachments = (result.Results ?? []).map(r => ({
        ID: r.ID,
        ArtifactID: r.ArtifactID,
        Name: r.Artifact,
        DisplayOrder: r.DisplayOrder
      }));
    }
  }

  /** Opens the slide-in preview drawer for the clicked outbound reply attachment chip. */
  protected openReplyAttachment(index: number): void {
    if (!this.replyAttachmentDrawer || this.replyAttachments.length === 0) return;
    const chips: MessageAttachmentChip[] = this.replyAttachments.map(a => ({
      AttachmentID: a.ID,
      ArtifactID: a.ArtifactID,
      Filename: a.Name,
      MimeType: '',
      SizeBytes: 0,
      DisplayOrder: a.DisplayOrder
    }));
    this.replyAttachmentDrawer.openWith(chips, index);
  }

  /** Opens the hidden file input from the Attach button. */
  protected triggerAttachmentPicker(input: HTMLInputElement): void {
    this.attachmentError = '';
    input.click();
  }

  /**
   * Uploads the picked file(s) through the server-side `Attach Reply File` action — the browser
   * cannot write attachment rows directly (UI role has no create permission on the join entity by
   * design; the action validates tenant scope + message state server-side). 25MB/file is enforced
   * here for an instant message and again server-side as the real gate.
   */
  protected async onAttachmentFilesSelected(input: HTMLInputElement): Promise<void> {
    const files = Array.from(input.files ?? []);
    input.value = ''; // allow re-picking the same file after a failure/remove
    if (files.length === 0 || !this.selectedMessage) return;

    const maxBytes = 25 * 1024 * 1024;
    this.isUploadingAttachment = true;
    this.attachmentError = '';
    try {
      for (const file of files) {
        if (file.size > maxBytes) {
          this.attachmentError = `"${file.name}" is ${(file.size / 1024 / 1024).toFixed(1)}MB — the limit is 25MB per file.`;
          continue;
        }
        const base64 = await this.fileToBase64(file);
        const result = await this.runReplyAttachmentAction('Attach Reply File', [
          { Name: 'ChannelMessageID', Value: this.selectedMessage.ID, Type: 'Input' },
          { Name: 'FileName', Value: file.name, Type: 'Input' },
          { Name: 'ContentType', Value: file.type || 'application/octet-stream', Type: 'Input' },
          { Name: 'ContentBase64', Value: base64, Type: 'Input' },
        ]);
        if (!result.ok) {
          this.attachmentError = result.message || `Failed to attach "${file.name}".`;
          continue;
        }
        const attachmentId = result.outputs.find(o => o.Name === 'AttachmentID')?.Value;
        const artifactId = result.outputs.find(o => o.Name === 'ArtifactID')?.Value;
        if (typeof attachmentId === 'string' && typeof artifactId === 'string') {
          const nextOrder = this.replyAttachments.reduce((max, a) => Math.max(max, a.DisplayOrder), -1) + 1;
          this.replyAttachments = [
            ...this.replyAttachments,
            { ID: attachmentId, ArtifactID: artifactId, Name: file.name, DisplayOrder: nextOrder }
          ];
        }
      }
    } finally {
      this.isUploadingAttachment = false;
    }
  }

  /** Removes one staff-attached file (and its backing artifact) via the server-side action. */
  protected async removeReplyAttachment(attachment: { ID: string; Name: string }): Promise<void> {
    this.attachmentError = '';
    const result = await this.runReplyAttachmentAction('Remove Reply Attachment', [
      { Name: 'AttachmentID', Value: attachment.ID, Type: 'Input' },
    ]);
    if (!result.ok) {
      this.attachmentError = result.message || `Failed to remove "${attachment.Name}".`;
      return;
    }
    this.replyAttachmentDrawer?.close();
    this.replyAttachments = this.replyAttachments.filter(a => a.ID !== attachment.ID);
  }

  /** Reads a File as raw base64 (no data-URI prefix). */
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        resolve(dataUrl.substring(dataUrl.indexOf(',') + 1));
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  /**
   * Run a server MJ Action by name (same pattern as ticket-detail's runAction): resolve the ID by
   * name, invoke via GraphQLActionClient with skipActionLog (base64 payloads are too large for
   * ActionExecutionLog rows), parse Output params from either array or index-keyed-object shape.
   */
  private async runReplyAttachmentAction(
    actionName: string,
    params: Array<{ Name: string; Value: unknown; Type: 'Input' }>,
  ): Promise<{ ok: boolean; message?: string; outputs: Array<{ Name: string; Value: unknown }> }> {
    const provider = Metadata.Provider as GraphQLDataProvider;
    const rv = new RunView();
    const actionRes = await rv.RunView<{ ID: string }>({
      EntityName: 'MJ: Actions',
      ExtraFilter: `Name = '${actionName.replace(/'/g, "''")}'`,
      ResultType: 'simple',
    });
    const actionId = actionRes.Success && actionRes.Results.length > 0 ? actionRes.Results[0].ID : null;
    if (!actionId) {
      return { ok: false, message: `Action "${actionName}" not found in metadata`, outputs: [] };
    }
    const client = new GraphQLActionClient(provider);
    const result = await client.RunAction(actionId, params as never, true);
    const raw: unknown = result?.Result;
    const candidates: unknown[] = Array.isArray(raw)
      ? raw
      : raw && typeof raw === 'object' ? Object.values(raw) : [];
    const outputs = candidates.filter((o): o is { Name: string; Value: unknown } =>
      !!o && typeof o === 'object' && typeof (o as { Name?: unknown }).Name === 'string');
    return { ok: !!result?.Success, message: result?.Message, outputs };
  }

  /**
   * Load a single full ChannelMessageEntity by ID (for detail view / save operations)
   */
  private async loadMessageEntity(id: string): Promise<ChannelMessageEntityExtendedClient | null> {
    const cached = this.entityCache.get(id);
    if (cached) return cached;

    const rv = new RunView();
    const result = await rv.RunView<ChannelMessageEntityExtendedClient>({
      EntityName: 'Channel Messages',
      ExtraFilter: `ID = '${id}'`,
      ResultType: 'entity_object',
      MaxRows: 1
    });
    if (result?.Success && result.Results.length > 0) {
      const entity = result.Results[0];
      this.entityCache.set(id, entity);
      return entity;
    }
    return null;
  }

  /**
   * Load all messages in the same thread for the context bar.
   * Caches results by ThreadID.
   */
  private async loadThreadContext(message: IzzyChannelMessageEntity): Promise<void> {
    const threadId = message.ThreadID;
    if (!threadId) {
      this.threadContextMessages = [];
      return;
    }

    // Check cache first
    if (this.threadContextCache.has(threadId)) {
      this.threadContextMessages = this.threadContextCache.get(threadId)!;
      return;
    }

    this.isThreadContextLoading = true;
    try {
      const rv = new RunView();
      const result = await rv.RunView<MessageListItem>({
        EntityName: 'Channel Messages',
        ExtraFilter: `ThreadID = '${threadId}'`,
        OrderBy: 'ReceivedAt ASC',
        ResultType: 'simple',
        Fields: ['Sender', 'Subject', 'MessageContentPreview', 'GenerationStatus',
                 'ApprovalStatus', 'ErrorType', 'SentAt', 'ForcedGeneration',
                 'ReceivedAt', '__mj_UpdatedAt', 'ApprovedReplyContent', 'GeneratedReplyContent',
                 'ThreadID', 'OutboundType'],
        MaxRows: 50
      });

      if (result?.Success && result.Results) {
        this.threadContextMessages = result.Results;
        this.threadContextCache.set(threadId, result.Results);
      } else {
        this.threadContextMessages = [];
      }
    } catch (error) {
      console.error('Error loading thread context:', error);
      this.threadContextMessages = [];
    } finally {
      this.isThreadContextLoading = false;
    }
  }

  async selectMessageByIndex(index: number): Promise<void> {
    if (index >= 0 && index < this.messages.length) {
      await this.selectMessage(this.messages[index], index);
    }
  }

  clearSelection(): void {
    this.selectedIds.clear();
    this.selectAllChecked = false;
  }

  deselectMessage(): void {
    this.replyAttachmentDrawer?.close();
    this.selectedMessage = null;
    this.selectedIndex = -1;
    this.isEditMode = false;
    this.isWriteMode = false;
    this.showDiff = false;
    this.diffHtml = '';
    this.syncMessagePresence();
    this.updateUserState();
  }

  async navigateNext(): Promise<void> {
    if (this.messages.length === 0) return;
    if (this.selectedIndex < this.messages.length - 1) {
      await this.selectMessageByIndex(this.selectedIndex + 1);
    }
  }

  async navigatePrevious(): Promise<void> {
    if (this.messages.length === 0) return;
    if (this.selectedIndex > 0) {
      await this.selectMessageByIndex(this.selectedIndex - 1);
    }
  }

  async selectNextMessage(): Promise<void> {
    if (this.messages.length === 0) return;

    const nextIndex = this.selectedIndex < this.messages.length - 1 ? this.selectedIndex + 1 : 0;
    await this.selectMessage(this.messages[nextIndex], nextIndex);
  }

  async selectPreviousMessage(): Promise<void> {
    if (this.messages.length === 0) return;

    const prevIndex = this.selectedIndex > 0 ? this.selectedIndex - 1 : this.messages.length - 1;
    await this.selectMessage(this.messages[prevIndex], prevIndex);
  }

  // ========== Bulk Selection ==========

  toggleMessageSelection(messageOrId: MessageListItem | string): void {
    const id = typeof messageOrId === 'string' ? messageOrId : messageOrId.ID;
    if (this.selectedIds.has(id)) {
      // Deselecting is always allowed — a row that became ineligible while selected
      // (approved elsewhere, send completed) must still be removable.
      this.selectedIds.delete(id);
    } else {
      // Selection feeds the bulk actions, so adding enforces the same eligibility the
      // checkbox renders with — the spacebar shortcut reaches here without a checkbox.
      const message = typeof messageOrId === 'string'
        ? this.messages.find(m => m.ID === messageOrId)
        : messageOrId;
      if (!message || !this.canApprove(message)) return;
      this.selectedIds.add(id);
    }
    this.updateSelectAllState();
  }

  isMessageSelected(message: MessageListItem): boolean {
    return this.selectedIds.has(message.ID);
  }

  toggleSelectAll(): void {
    if (this.selectAllChecked) {
      this.selectedIds.clear();
    } else {
      this.messages.forEach(m => {
        if (!m.isThreadSibling && this.canApprove(m)) {
          this.selectedIds.add(m.ID);
        }
      });
    }
    this.selectAllChecked = !this.selectAllChecked;
  }

  private updateSelectAllState(): void {
    const selectableMessages = this.messages.filter(m => !m.isThreadSibling && this.canApprove(m));
    this.selectAllChecked = selectableMessages.length > 0 &&
      selectableMessages.every(m => this.selectedIds.has(m.ID));
  }

  get selectedCount(): number {
    return this.selectedIds.size;
  }

  // ========== Approval Actions ==========

  canApprove(message: MessageListItem): boolean {
    // Dispositionable = Pending/Escalated AND never sent. A message that already went out
    // (e.g. an Escalated-but-sent row from the pre-guard era) must not look actionable:
    // this gates the row checkbox, select-all, the swipe affordances, and the
    // needs-action highlight. SentAt is stamped on send success (rows sent before
    // 2026-07-30 lack it — the entity-level SentContent check catches those at commit).
    return (message.ApprovalStatus === 'Pending' || message.ApprovalStatus === 'Escalated')
      && !message.SentAt;
  }

  async approveMessage(messageOrId?: ChannelMessageEntityExtendedClient | string): Promise<void> {
    const msg = await this.resolveMessageEntity(messageOrId);
    if (!msg) return;
    // Commit-point guard: approving sends immediately, so no entry path (button,
    // shortcut, swipe, bulk) may commit a message the entity's rules disallow.
    if (!msg.IsApprovable) return;
    msg.Approve(this.feedbackText || undefined);
    await this.saveAndAdvance(msg);
  }

  async rejectMessage(messageOrId?: ChannelMessageEntityExtendedClient | string): Promise<void> {
    const msg = await this.resolveMessageEntity(messageOrId);
    if (!msg) return;
    if (!msg.IsRejectable) return;
    msg.Reject(this.feedbackText || undefined);
    await this.saveAndAdvance(msg);
  }

  async markAsNotRequired(messageOrId?: ChannelMessageEntityExtendedClient | string): Promise<void> {
    const msg = await this.resolveMessageEntity(messageOrId);
    if (!msg) return;

    this.showConfirmDialog(
      'Mark as Not Required',
      'Please provide a reason for marking this message as Not Required.',
      async () => {
        const reason = this.confirmDialogInputValue.trim();
        if (!reason) return;
        msg.MarkAsNotRequired(reason);
        await this.saveAndAdvance(msg);
      },
      'Confirm',
      true,
      { label: 'Reason' }
    );
  }

  private async resolveMessageEntity(messageOrId?: ChannelMessageEntityExtendedClient | string): Promise<ChannelMessageEntityExtendedClient | null> {
    if (!messageOrId) return this.selectedMessage;
    if (typeof messageOrId === 'string') return this.loadMessageEntity(messageOrId);
    return messageOrId;
  }

  canEscalateMessage(message?: ChannelMessageEntityExtendedClient): boolean {
    const msg = message || this.selectedMessage;
    return msg?.IsEscalatable === true;
  }

  async escalateMessage(message?: ChannelMessageEntityExtendedClient): Promise<void> {
    const msg = message || this.selectedMessage;
    if (!msg || !this.canEscalateMessage(msg)) return;

    // Attribution (who escalated) is stamped server-side from the request context.
    msg.Escalate(this.feedbackText || undefined);

    await this.saveAndAdvance(msg);
  }

  async openManualResponse(): Promise<void> {
    if (!this.selectedMessage) return;
    // Open message editor for human-written response
    this.enterEditMode();
  }

  forceAIGeneration(): void {
    if (!this.selectedMessage) return;

    this.forceGenerateTarget = this.selectedMessage;

    this.showConfirmDialog(
      'Force AI Generation',
      'This will override the filter and generate an AI response. The response will require manual approval before sending.',
      async () => {
        const target = this.forceGenerateTarget;
        if (!target) return;

        this.isReprocessing = true;
        try {
          // A save may still be in flight (e.g. the user escalated a moment ago). BaseEntity.Save()
          // shares one in-flight unit of work, so mutating and saving now would silently piggyback
          // on that save and never persist these fields. Wait it out, then reload so we mutate the
          // record as it actually landed.
          await target.EnsureSaveComplete();
          await target.Load(target.ID);

          target.ForceAIGeneration();

          if (!await target.Save()) {
            // Save() reports failure by returning false rather than throwing, so without this the
            // catch below is bypassed and the user gets a success message for a write that failed.
            throw new Error(target.LatestResult?.Message || 'Save returned false');
          }

          this.showSuccess('AI generation started - response will require manual approval');
          await this.refreshStatsAndData();
        } catch (error) {
          console.error('Error forcing AI generation:', error);
          this.errorMessage = 'Failed to force AI generation. Please try again.';
        } finally {
          this.isReprocessing = false;
        }
      },
      'Generate',
      false
    );
  }

  async approveWithEdits(): Promise<void> {
    if (!this.selectedMessage) return;

    this.selectedMessage.ApproveWithEdits(this.editedContent, this.isWriteMode, this.feedbackText || undefined);

    await this.saveAndAdvance(this.selectedMessage);
  }

  /**
   * Save draft edits without approving (doesn't send the message)
   */
  async saveDraft(): Promise<void> {
    if (!this.selectedMessage) return;

    this.isSaving = true;
    try {
      this.selectedMessage.SaveDraftContent(this.editedContent, this.isWriteMode);
      this.stampPlannedCC(this.selectedMessage);
      await this.selectedMessage.Save();

      this.showSuccess('Draft saved');
      this.isEditMode = false;
      this.isWriteMode = false;

      // Recompute diff for display
      this.computeHtmlDiff(
        this.selectedMessage.GeneratedReplyContent || '',
        this.selectedMessage.ApprovedReplyContent || ''
      );
    } catch (error) {
      console.error('Error saving draft:', error);
      this.errorMessage = 'Failed to save draft. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Initializes the recipient controls for a newly selected message: computes the reply-all
   * prefill (inbound To+CC minus our mailbox and the sender), and opens the composer in the
   * mode the channel's ReplyAllDefault setting dictates. Display-level only — the server
   * re-applies full suppression and the recipient cap at send time.
   */
  private initReplyRecipients(entity: ChannelMessageEntityExtendedClient): void {
    // Staff drafts (Manual Messages): no reply-all context exists (no inbound half); the CC
    // input starts from the composer's stored intent (SentCCRecipients holds pre-send CC).
    if (this.isStaffDraft(entity)) {
      this.replyAllPrefill = [];
      this.replyAllAvailable = false;
      const remembered = this.ccMemory.get(this.ccMemoryKey(entity.ID));
      this.replyMode = remembered?.replyMode ?? 'reply';
      this.ccInput = remembered?.ccInput ?? (entity.SentCCRecipients || '');
      return;
    }
    const channel = IzzyMetadataEngine.Instance.GetChannel(entity.ChannelID);
    this.replyAllPrefill = entity.GetReplyAllPrefill(channel?.ExternalIdentifier);
    this.replyAllAvailable = this.replyAllPrefill.length > 0;

    // Precedence: what the approver typed this session (ccMemory) > the intent persisted on the
    // entity by an earlier composer save (pre-send SentCCRecipients — same convention staff
    // drafts already use) > the channel's reply/reply-all default. A sent message's
    // SentCCRecipients is the audit of what went out, never a prefill.
    const remembered = this.ccMemory.get(this.ccMemoryKey(entity.ID));
    if (remembered) {
      this.replyMode = remembered.replyMode;
      this.ccInput = remembered.ccInput;
      return;
    }
    const plannedCC = !entity.SentAt ? (entity.SentCCRecipients || '') : '';
    if (plannedCC) {
      this.ccInput = plannedCC;
      this.replyMode = entity.IsReplyAllCC(plannedCC, channel?.ExternalIdentifier) ? 'replyAll' : 'reply';
      return;
    }
    const defaultReplyAll = this.replyAllAvailable &&
      IzzyMetadataEngine.Instance.GetSettingValueBoolean('ReplyAllDefault', entity.ChannelID, false);
    this.replyMode = defaultReplyAll ? 'replyAll' : 'reply';
    this.ccInput = defaultReplyAll ? this.replyAllPrefill.join(', ') : '';
  }

  /** Cc box edit: remember the approver's intent for this message so a re-select can't blank it. */
  protected rememberCc(value: string): void {
    if (!this.selectedMessage) return;
    this.ccMemory.set(this.ccMemoryKey(this.selectedMessage.ID), { ccInput: value, replyMode: this.replyMode });
  }

  /**
   * Persists the composer's current Cc onto the entity as pre-send intent, so it survives a
   * browser reload (initReplyRecipients restores it). Only for the message the composer is
   * showing (the Cc box belongs to it), and never for a sent message — after send,
   * SentCCRecipients is the server's record of what actually went out. The ticket-view
   * composer does not read pre-send SentCCRecipients today; it seeds from reply-all only.
   */
  private stampPlannedCC(message: ChannelMessageEntityExtendedClient): void {
    if (!this.selectedMessage || !UUIDsEqual(message.ID, this.selectedMessage.ID) || message.SentAt) return;
    message.SetPlannedCCRecipients(this.ccInput.split(',').map(a => a.trim()).filter(a => a.length > 0));
  }

  // ========== Manual Messages (issue #289): compose + follow-up ==========

  /**
   * A staff-originated row (Manual Messages): non-Forward OutboundType with NO inbound half.
   * OutboundType is stamped at CREATION on rows made by the Create Manual Message action
   * ('New' = compose, 'Reply' = follow-up); AI replies only receive OutboundType='Reply' at
   * send time, and they always carry the inbound MessageContent — staff rows never do.
   */
  protected isStaffDraft(msg: ChannelMessageEntityExtendedClient | null): boolean {
    return !!msg?.OutboundType && msg.OutboundType !== 'Forward' && !msg.MessageContent;
  }

  /** List-chip label — list items carry MessageContentPreview as the inbound-half signal. */
  protected staffChipLabel(item: MessageListItem): string | null {
    if (!item.OutboundType || item.OutboundType === 'Forward' || item.MessageContentPreview) return null;
    return item.OutboundType === 'New' ? 'Composed' : 'Follow-up';
  }

  /**
   * Who the list row is "about": inbound rows show the sender; staff rows show the person
   * the message goes TO (their Sender is just the channel mailbox — useless in a list).
   */
  protected listDisplayName(item: MessageListItem): string {
    return this.staffChipLabel(item) ? (item.Recipient || item.Sender) : item.Sender;
  }

  /** Action-bar labels: staff drafts say what really happens (design pick 2026-08-25). */
  protected approveLabel(): string {
    if (this.isSaving && this.savingAction === 'Approved') return 'Sending…';
    return this.isStaffDraft(this.selectedMessage) ? 'Approve & Send' : 'Approve';
  }

  protected dismissLabel(): string {
    if (this.isSaving && this.savingAction === 'Rejected') return 'Dismissing…';
    return this.isStaffDraft(this.selectedMessage) ? 'Discard draft' : 'Dismiss';
  }

  protected showComposeDialog = false;
  protected composeMode: 'new' | 'followup' = 'new';
  protected composeTo = '';
  protected composeCc = '';
  protected composeSubject = '';
  protected composeBody = '';
  /** Initial editor content for the compose dialog; stays '' so the live body never round-trips back into Quill. */
  protected readonly composeBodySeed = '';
  protected composeError = '';
  protected isComposeSubmitting = false;
  /** Follow-up lane: the threadable message the draft replies onto. */
  protected composeParent: ChannelMessageEntityExtendedClient | null = null;

  protected openComposeDialog(): void {
    if (!this.selectedChannel) return;
    this.composeMode = 'new';
    this.composeParent = null;
    this.composeTo = '';
    this.composeCc = '';
    this.composeSubject = '';
    this.composeBody = '';
    this.composeError = '';
    this.showComposeDialog = true;
  }

  protected openFollowUpDialog(): void {
    if (!this.selectedMessage?.ExternalSystemRecordID) return;
    this.composeMode = 'followup';
    this.composeParent = this.selectedMessage;
    this.composeTo = this.selectedMessage.Sender;
    this.composeCc = '';
    this.composeSubject = `Re: ${this.selectedMessage.Subject ?? ''}`;
    this.composeBody = '';
    this.composeError = '';
    this.showComposeDialog = true;
  }

  protected closeComposeDialog(): void {
    if (this.isComposeSubmitting) return;
    this.showComposeDialog = false;
  }

  protected canSubmitCompose(): boolean {
    if (this.isComposeSubmitting) return false;
    if (!this.composeBody.trim()) return false;
    if (this.composeMode === 'followup') return !!this.composeParent;
    return !!this.composeTo.trim() && !!this.composeSubject.trim();
  }

  /**
   * Create the staff draft via the server-side Create Manual Message action, then reload and
   * select it. Nothing is sent here — the draft is born Pending and sends on approval.
   */
  protected async submitCompose(): Promise<void> {
    if (!this.canSubmitCompose()) return;
    this.isComposeSubmitting = true;
    this.composeError = '';
    try {
      const params: Array<{ Name: string; Value: unknown; Type: 'Input' }> = [
        // Editor HTML carries &nbsp; between words; the same cleanup the approve path uses.
        { Name: 'Body', Value: MessageEditorComponent.CleanNbspForSend(this.composeBody), Type: 'Input' },
      ];
      const cc = this.composeCc.trim();
      if (cc) {
        params.push({ Name: 'CCRecipients', Value: cc, Type: 'Input' });
      }
      if (this.composeMode === 'followup') {
        params.push({ Name: 'ParentMessageID', Value: this.composeParent!.ID, Type: 'Input' });
      } else {
        params.push({ Name: 'ChannelID', Value: this.selectedChannel!.ID, Type: 'Input' });
        params.push({ Name: 'Recipient', Value: this.composeTo.trim(), Type: 'Input' });
        params.push({ Name: 'Subject', Value: this.composeSubject.trim(), Type: 'Input' });
      }
      const result = await this.runReplyAttachmentAction('Create Manual Message', params);
      if (!result.ok) {
        this.composeError = result.message || 'Failed to create the draft.';
        return;
      }
      const newId = result.outputs.find(o => o.Name === 'MessageID')?.Value as string | undefined;
      this.showComposeDialog = false;
      this.showSuccess('Draft created — approve it to send');
      // The draft is born Pending: make sure the filter can show it, reload, select it.
      if (this.approvalStatusFilter !== 'All' && this.approvalStatusFilter !== 'Pending') {
        this.approvalStatusFilter = 'Pending';
      }
      await this.loadData();
      this.loadStats();
      if (newId) {
        const index = this.messages.findIndex(m => m.ID === newId);
        if (index >= 0) {
          await this.selectMessage(this.messages[index], index);
        }
      }
    } catch (error) {
      this.composeError = error instanceof Error ? error.message : String(error);
    } finally {
      this.isComposeSubmitting = false;
    }
  }

  /** Outlook-style toggle: Reply All fills CC with the prefill; Reply clears it. Manual edits after toggling are the approver's call. */
  protected setReplyMode(mode: 'reply' | 'replyAll'): void {
    this.replyMode = mode;
    this.ccInput = mode === 'replyAll' ? this.replyAllPrefill.join(', ') : '';
    this.rememberCc(this.ccInput);
  }

  private async saveAndAdvance(message: ChannelMessageEntityExtendedClient): Promise<void> {
    this.isSaving = true;
    // Approving actually SENDS the reply inside Save() — that's seconds of real work
    // (more with attachments), so tell the approver what's happening rather than
    // leaving a silently-dimmed action bar.
    this.savingLabel = message.ApprovalStatus === 'Approved'
      ? (this.replyAttachments.length > 0
          ? `Sending reply with ${this.replyAttachments.length} file${this.replyAttachments.length === 1 ? '' : 's'}…`
          : 'Sending reply…')
      : 'Saving…';
    this.savingAction = message.ApprovalStatus || '';
    try {
      // Record the composer's CC list as the approver's explicit intent on EVERY unsent save,
      // not just the approving one: on approve the server send path reads SentCCRecipients,
      // applies suppression + the cap, and overwrites it with what was actually sent; on
      // escalate/reject it simply persists the typed Cc so a later re-open (or the ticket
      // view) starts from it instead of a blank box.
      this.stampPlannedCC(message);
      await message.Save();
      if (message.ApprovalStatus === 'Approved' || message.ApprovalStatus === 'Rejected') {
        this.ccMemory.delete(this.ccMemoryKey(message.ID)); // leaves the queue — nothing left to remember
      }

      this.showSuccess(`Message ${message.ApprovalStatus?.toLowerCase()}`);

      this.interaction.emit({
        type: 'approvalChange',
        messageId: message.ID,
        newStatus: message.ApprovalStatus
      });

      await this.loadStats();

      // Auto-advance to next message
      if (this.autoAdvance) {
        const currentIndex = this.messages.findIndex(m => m.ID === message.ID);
        this.messages = this.messages.filter(m => m.ID !== message.ID);

        if (this.messages.length > 0) {
          const nextIndex = Math.min(currentIndex, this.messages.length - 1);
          await this.selectMessage(this.messages[nextIndex], nextIndex);
        } else {
          // No more messages in current view - clear both bulk selection and message detail
          this.clearSelection();
          this.deselectMessage();
        }
      } else {
        await this.loadData();
      }
    } catch (error) {
      console.error('Error saving message:', error);
      this.errorMessage = 'Failed to save. Please try again.';
    } finally {
      this.isSaving = false;
      this.savingLabel = '';
      this.savingAction = '';
      this.feedbackText = '';
    }
  }

  // ========== Bulk Actions ==========

  bulkApprove(): void {
    if (this.selectedIds.size === 0) return;

    this.showConfirmDialog(
      'Bulk Approve',
      `Are you sure you want to approve ${this.selectedIds.size} message${this.selectedIds.size === 1 ? '' : 's'}?`,
      async () => this.executeBulkApprove(),
      'Approve All',
      false
    );
  }

  private async executeBulkApprove(): Promise<void> {
    this.isSaving = true;
    try {
      const idsToApprove = this.messages.filter(m => this.selectedIds.has(m.ID)).map(m => m.ID);

      for (const id of idsToApprove) {
        const msg = await this.loadMessageEntity(id);
        if (!msg) continue;
        msg.Approve();
        await msg.Save();
      }

      this.showSuccess(`${idsToApprove.length} message${idsToApprove.length === 1 ? '' : 's'} approved`);
      this.selectedIds.clear();
      this.selectAllChecked = false;

      await this.refreshStatsAndData();
    } catch (error) {
      console.error('Error in bulk approve:', error);
      this.errorMessage = 'Failed to approve some messages.';
    } finally {
      this.isSaving = false;
    }
  }

  bulkReject(): void {
    if (this.selectedIds.size === 0) return;

    this.showConfirmDialog(
      'Bulk Reject',
      `Are you sure you want to reject ${this.selectedIds.size} messages?`,
      async () => this.executeBulkReject(),
      'Reject All',
      true
    );
  }

  private async executeBulkReject(): Promise<void> {
    this.isSaving = true;
    try {
      const idsToReject = this.messages.filter(m => this.selectedIds.has(m.ID)).map(m => m.ID);

      for (const id of idsToReject) {
        const msg = await this.loadMessageEntity(id);
        if (!msg) continue;
        msg.Reject();
        await msg.Save();
      }

      this.showSuccess(`${idsToReject.length} message${idsToReject.length === 1 ? '' : 's'} rejected`);
      this.selectedIds.clear();
      this.selectAllChecked = false;

      await this.refreshStatsAndData();
    } catch (error) {
      console.error('Error in bulk reject:', error);
      this.errorMessage = 'Failed to reject some messages.';
    } finally {
      this.isSaving = false;
    }
  }

  bulkNotRequired(): void {
    if (this.selectedIds.size === 0) return;

    this.showConfirmDialog(
      'Bulk Not Required',
      `Are you sure you want to mark ${this.selectedIds.size} message${this.selectedIds.size === 1 ? '' : 's'} as Not Required?`,
      async () => this.executeBulkNotRequired(),
      'Mark All Not Required',
      true,
      { label: 'Reason (applies to all selected messages)' }
    );
  }

  private async executeBulkNotRequired(): Promise<void> {
    const reason = this.confirmDialogInputValue.trim();
    if (!reason) return;

    this.isSaving = true;
    try {
      const ids = this.messages.filter(m => this.selectedIds.has(m.ID)).map(m => m.ID);

      for (const id of ids) {
        const msg = await this.loadMessageEntity(id);
        if (!msg) continue;
        msg.MarkAsNotRequired(reason);
        await msg.Save();
      }

      this.showSuccess(`${ids.length} message${ids.length === 1 ? '' : 's'} marked as not required`);
      this.selectedIds.clear();
      this.selectAllChecked = false;

      await this.refreshStatsAndData();
    } catch (error) {
      console.error('Error in bulk not required:', error);
      this.errorMessage = 'Failed to mark some messages as not required.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Thread Bulk Actions ==========

  async bulkApproveThread(group: ThreadGroup): Promise<void> {
    // Approvable-only, not status-only: a Pending message still generating (or with no
    // content) must not be swept up — approveMessage's commit guard would silently skip
    // it and the success toast would lie about what happened.
    const pendingIds = group.messages
      .filter(m => ChannelMessageEntityExtendedClient.IsApprovableState(m))
      .map(m => m.ID);

    if (pendingIds.length === 0) return;

    if (pendingIds.length > 5) {
      this.showConfirmDialog(
        'Approve Thread',
        `Approve all ${pendingIds.length} pending messages in this thread?`,
        async () => this.executeBulkThreadApprove(pendingIds),
        'Approve All',
        false
      );
    } else {
      await this.executeBulkThreadApprove(pendingIds);
    }
  }

  private async executeBulkThreadApprove(ids: string[]): Promise<void> {
    this.isSaving = true;
    try {
      for (const id of ids) {
        await this.approveMessage(id);
      }
      this.showSuccess(`${ids.length} thread messages approved`);
      await this.refreshStatsAndData();
    } catch (error) {
      console.error('Error in bulk thread approve:', error);
      this.errorMessage = 'Failed to approve some thread messages.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Confirm Dialog Methods ==========

  protected showConfirmDialog(
    title: string,
    message: string,
    action: () => Promise<void>,
    buttonText: string = 'Yes',
    isDanger: boolean = false,
    inputConfig?: { label: string }
  ): void {
    this.confirmDialogTitle = title;
    this.confirmDialogMessage = message;
    this.confirmDialogAction = action;
    this.confirmDialogButtonText = buttonText;
    this.confirmDialogDanger = isDanger;
    this.confirmDialogHasInput = !!inputConfig;
    this.confirmDialogInputLabel = inputConfig?.label ?? '';
    this.confirmDialogInputValue = '';
    this.confirmDialogVisible = true;
  }

  protected async confirmDialogYes(): Promise<void> {
    if (this.confirmDialogHasInput && !this.confirmDialogInputValue.trim()) {
      return;
    }
    if (this.confirmDialogAction) {
      await this.confirmDialogAction();
    }
    this.closeConfirmDialog();
  }

  protected closeConfirmDialog(): void {
    this.confirmDialogVisible = false;
    this.confirmDialogAction = null;
    this.confirmDialogHasInput = false;
    this.confirmDialogInputValue = '';
    this.confirmDialogInputLabel = '';
    this.forceGenerateTarget = null;
  }

  // ========== Edit Mode ==========

  enterEditMode(): void {
    if (!this.selectedMessage) return;
    this.isEditMode = true;
    this.isWriteMode = false;
    // Edit the GeneratedReplyContent (AI's draft)
    this.editedContent = this.selectedMessage.GeneratedReplyContent || '';
    this.showDiff = false;
    this.diffHtml = '';
    this.isReasoningExpanded = false;
  }

  enterWriteMode(): void {
    if (!this.selectedMessage) return;
    this.isEditMode = true;
    this.isWriteMode = true;
    // Staff drafts (and prior manual writes) live in ApprovedReplyContent — editing must seed
    // the current content, not a blank pane that would wipe it on save.
    this.editedContent = this.selectedMessage.ApprovedReplyContent || '';
    this.showDiff = false;
    this.diffHtml = '';
    this.isReasoningExpanded = false;
  }

  /** Unified edit/write: if AI has generated a reply, edit it; otherwise start a blank response */
  enterEditOrWriteMode(): void {
    if (!this.selectedMessage) return;
    if (this.selectedMessage.GeneratedReplyContent) {
      this.enterEditMode();
    } else {
      this.enterWriteMode();
    }
  }

  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;
    if (this.isEditMode && this.selectedMessage) {
      this.editedContent = this.selectedMessage.GeneratedReplyContent || '';
    }
    this.showDiff = false;
  }

  cancelEdit(): void {
    this.isEditMode = false;
    this.showDiff = false;
    this.diffHtml = '';
    if (this.selectedMessage) {
      this.editedContent = this.selectedMessage.GeneratedReplyContent || '';
    }
  }

  onEditedContentChange(): void {
    // Recompute diff if showing
    if (this.showDiff && this.selectedMessage) {
      this.computeHtmlDiff(
        this.selectedMessage.GeneratedReplyContent || '',
        this.editedContent || ''
      );
    }
  }

  toggleDiff(): void {
    this.showDiff = !this.showDiff;
    if (this.showDiff && this.selectedMessage) {
      this.computeHtmlDiff(
        this.selectedMessage.GeneratedReplyContent || '',
        this.editedContent || ''
      );
    }
  }

  showApprovedDiff(): void {
    if (!this.selectedMessage) return;

    // Compare GeneratedReplyContent vs ApprovedReplyContent
    this.computeHtmlDiff(
      this.selectedMessage.GeneratedReplyContent || '',
      this.selectedMessage.ApprovedReplyContent || ''
    );
    this.showDiff = true;
  }

  /**
   * Set the reply view mode (for reviewing messages with ApprovedReplyContent)
   */
  setReplyViewMode(mode: 'approved' | 'generated' | 'diff'): void {
    this.replyViewMode = mode;
    if (mode === 'diff' && this.selectedMessage) {
      // Compute diff when switching to diff mode
      this.computeHtmlDiff(
        this.selectedMessage.GeneratedReplyContent || '',
        this.selectedMessage.ApprovedReplyContent || ''
      );
    }
  }

  /**
   * Compute HTML diff using htmldiff-js
   */
  private computeHtmlDiff(original: string, modified: string): void {
    try {
      const diff = HtmlDiff.execute(original, modified);
      this.diffHtml = this.sanitizer.bypassSecurityTrustHtml(diff);
    } catch (error) {
      console.error('Error computing HTML diff:', error);
      this.diffHtml = this.sanitizer.bypassSecurityTrustHtml(modified);
    }
  }

  hasEdits(): boolean {
    if (!this.selectedMessage) return false;
    return this.editedContent !== this.selectedMessage.GeneratedReplyContent;
  }

  /**
   * Check if the reply was edited (ApprovedReplyContent is not null)
   */
  wasEdited(message: IzzyChannelMessageEntity): boolean {
    return !!message.ApprovedReplyContent;
  }

  // ========== WYSIWYG Editor Integration ==========

  /**
   * Handle content changes from the MessageEditorComponent
   */
  onEditorContentChange(event: ContentChangeEvent): void {
    this.editedContent = event.content;
    // No need to manually compute diff - the editor handles that
  }

  /**
   * Handle save/approve from the MessageEditorComponent
   */
  async onEditorSave(event: SaveEvent): Promise<void> {
    if (!this.selectedMessage) return;

    if (event.approved) {
      // Delegate to entity subclass method for consistent behavior
      this.selectedMessage.ApproveWithEdits(event.content, this.isWriteMode, this.feedbackText);
      await this.saveAndAdvance(this.selectedMessage);
    }

    // Exit edit mode and collapse editor
    this.isEditMode = false;
    this.isWriteMode = false;
    this.isEditorExpanded = false;
  }

  /**
   * Handle save draft from the MessageEditorComponent
   */
  async onEditorSaveDraft(content: string): Promise<void> {
    if (!this.selectedMessage) return;

    this.isSaving = true;
    try {
      this.selectedMessage.SaveDraftContent(content, this.isWriteMode);
      this.editedContent = content;
      await this.selectedMessage.Save();

      this.showSuccess('Draft saved');

      // Exit edit mode
      this.isEditMode = false;
      this.isWriteMode = false;
      this.isEditorExpanded = false;

      // Recompute diff for display
      this.computeHtmlDiff(
        this.selectedMessage.GeneratedReplyContent || '',
        this.selectedMessage.ApprovedReplyContent || ''
      );
    } catch (error) {
      console.error('Error saving draft:', error);
      this.errorMessage = 'Failed to save draft. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Handle cancel from the MessageEditorComponent
   */
  onEditorCancel(): void {
    this.isEditMode = false;
    this.isEditorExpanded = false;
    this.showDiff = false;
    this.diffHtml = '';
    if (this.selectedMessage) {
      this.editedContent = this.selectedMessage.GeneratedReplyContent || '';
    }
  }

  /**
   * Handle expand/collapse toggle from the MessageEditorComponent
   */
  onEditorExpandChange(isExpanded: boolean): void {
    this.isEditorExpanded = isExpanded;
  }

  /**
   * Handle revert from the MessageEditorComponent
   */
  onEditorRevert(): void {
    if (this.selectedMessage) {
      this.editedContent = this.selectedMessage.EditBaselineContent;
    }
  }

  /**
   * Get the message format for the current message
   */
  getMessageFormat(): 'HTML' | 'PlainText' | 'Markdown' {
    if (!this.selectedMessage) return 'HTML';
    const format = this.selectedMessage.MessageFormat;
    if (format === 'HTML' || format === 'PlainText' || format === 'Markdown') {
      return format;
    }
    return 'HTML'; // Default to HTML
  }

  // ========== Filter Handlers ==========

  onApprovalStatusFilterChange(status: string): void {
    this.approvalStatusFilter = status;

    this.clearSelection();
    this.deselectMessage();
    this.updateUserState();
    this.loadData();
  }

  onGenerationStatusFilterChange(status: string): void {
    this.generationStatusFilter = status;

    this.deselectMessage(); // Clear selection when filter changes
    this.saveUserSettings();
    this.updateUserState();
    this.refreshStatsAndData();
  }

  // ========== Quick Filter (Stats Bar) ==========

  setApprovalStatusFilter(status: string): void {
    // Toggle: clicking the active filter resets to "All"
    this.approvalStatusFilter = this.approvalStatusFilter === status ? 'All' : status;

    this.clearSelection();
    this.deselectMessage();
    this.saveUserSettings();
    this.updateUserState();
    this.loadData();
  }

  filterByApprovalStatus(status: string): void {
    this.approvalStatusFilter = status;

    this.clearSelection();
    this.deselectMessage();
    this.saveUserSettings();
    this.updateUserState();
    this.loadData();
  }

  // ========== Panel Controls ==========

  togglePanelCollapse(): void {
    this.isPanelCollapsed = !this.isPanelCollapsed;
    this.saveUserSettings();
    this.updateUserState();
  }

  setPanelWidth(width: number): void {
    this.panelWidth = Math.max(250, Math.min(600, width)); // Clamp between 250-600px
    this.saveUserSettings();
    this.updateUserState();
  }

  // ========== Panel Resize ==========

  private isResizing = false;
  private resizeStartX = 0;
  private resizeStartWidth = 0;

  private boundOnResizeMove = this.onResizeMove.bind(this);
  private boundOnResizeEnd = this.onResizeEnd.bind(this);

  onResizeStart(event: MouseEvent): void {
    event.preventDefault();
    this.isResizing = true;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.panelWidth;
    document.addEventListener('mousemove', this.boundOnResizeMove);
    document.addEventListener('mouseup', this.boundOnResizeEnd);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }

  private onResizeMove(event: MouseEvent): void {
    if (!this.isResizing) return;
    const delta = event.clientX - this.resizeStartX;
    this.panelWidth = Math.max(250, Math.min(600, this.resizeStartWidth + delta));
  }

  private onResizeEnd(): void {
    if (!this.isResizing) return;
    this.isResizing = false;
    document.removeEventListener('mousemove', this.boundOnResizeMove);
    document.removeEventListener('mouseup', this.boundOnResizeEnd);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    this.saveUserSettings();
    this.updateUserState();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Fix for issue #63: Don't close dropdowns if there's an active text selection
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return; // User is selecting text, don't interfere with dropdown state
    }

    const target = event.target as HTMLElement;
    if (!target.closest('.assign-dropdown-container')) {
      this.isAssignDropdownOpen = false;
    }
    if (!target.closest('.thread-indicator-badge') && !target.closest('.thread-popover')) {
      this.activeThreadPopoverId = null;
    }
  }

  // ========== Search ==========

  private searchDebounceTimer: any;
  private lastExecutedSearch = '';

  @ViewChild('searchInput') searchInputRef?: ElementRef<HTMLInputElement>;

  /** '?' keyboard-shortcuts overlay visibility */
  showShortcutsOverlay = false;

  /** Row hover quick-action: assign by list-item ID (reuses assignToMe). */
  async assignMessageToMe(messageId: string): Promise<void> {
    const msg = await this.resolveMessageEntity(messageId);
    if (msg) {
      await this.assignToMe(msg);
    }
  }

  onSearchChange(query: string): void {
    this.searchQuery = query;
    // Debounce the server-side search. The body is searched through the full-text index, so
    // body-eligible input no longer needs extra slack.
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      if (this.searchQuery.trim() === this.lastExecutedSearch) return;
      this.lastExecutedSearch = this.searchQuery.trim();
      this.loadData();
    }, 300);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.lastExecutedSearch = '';
    clearTimeout(this.searchDebounceTimer);

    this.loadData();
  }

  /**
   * Get messages for display — search is now server-side, so no client-side filtering needed
   */
  get displayMessages(): MessageListItem[] {
    return this.messages;
  }

  /**
   * Build the threaded display list from the flat messages array.
   * Groups messages by ThreadID — 2+ messages become a ThreadGroup, singles stay standalone.
   * Interleaves expanded thread children after their header.
   */
  get displayItems(): DisplayItem[] {
    const filtered = this.displayMessages;

    // Group by ThreadID
    const threadMap = new Map<string, MessageListItem[]>();
    const standalones: MessageListItem[] = [];

    for (const msg of filtered) {
      const tid = msg.ThreadID;
      if (tid) {
        if (!threadMap.has(tid)) {
          threadMap.set(tid, []);
        }
        threadMap.get(tid)!.push(msg);
      } else {
        standalones.push(msg);
      }
    }

    // Build top-level items and their children
    const topLevel: DisplayItem[] = [];
    const childrenOf = new Map<string, DisplayItem[]>();

    for (const [threadId, messages] of threadMap) {
      if (messages.length === 1) {
        // Single-message thread — show as standalone
        topLevel.push({ type: 'message', message: messages[0] });
      } else {
        // Multi-message thread — create ThreadGroup
        const sorted = [...messages].sort((a, b) =>
          new Date(a.ReceivedAt).getTime() - new Date(b.ReceivedAt).getTime()
        );
        const latest = sorted[sorted.length - 1];
        const uniqueSenders = [...new Set(sorted.map(m => m.Sender))];

        const group: ThreadGroup = {
          type: 'thread',
          threadId,
          subject: sorted[0].Subject || '(No Subject)',
          participants: uniqueSenders,
          messages: sorted,
          latestMessage: latest,
          latestTimestamp: new Date(this.sortMode === 'received'
            ? latest.ReceivedAt : latest.__mj_UpdatedAt),
          pendingCount: sorted.filter(m => m.ApprovalStatus === 'Pending').length,
          escalatedCount: sorted.filter(m => m.ApprovalStatus === 'Escalated').length,
          approvableCount: sorted.filter(m => ChannelMessageEntityExtendedClient.IsApprovableState(m)).length,
          totalCount: sorted.length,
          isExpanded: this.expandedThreads.has(threadId)
        };

        topLevel.push({ type: 'thread', thread: group });

        if (group.isExpanded) {
          const children: DisplayItem[] = sorted.map(msg => ({
            type: 'message' as const,
            message: msg,
            isThreadChild: true,
            parentThread: group
          }));
          childrenOf.set(threadId, children);
        }
      }
    }

    // Add standalone messages
    for (const msg of standalones) {
      topLevel.push({ type: 'message', message: msg });
    }

    // Sort top-level items by timestamp DESC
    topLevel.sort((a, b) => {
      const tsA = a.type === 'thread' && a.thread
        ? a.thread.latestTimestamp.getTime()
        : new Date(this.getItemTimestamp(a)).getTime();
      const tsB = b.type === 'thread' && b.thread
        ? b.thread.latestTimestamp.getTime()
        : new Date(this.getItemTimestamp(b)).getTime();
      return tsB - tsA;
    });

    // Interleave children after their parent thread headers, inserting a
    // date-group header whenever the bucket changes. Buckets follow the same
    // timestamp the sort uses, so they're always contiguous — this composes
    // with infinite scroll for free (the getter recomputes over the full
    // accumulated list). Suppressed while searching: results read as one set.
    const grouping = !this.isSearchActive;
    const result: DisplayItem[] = [];
    let currentBucket = '';
    for (const item of topLevel) {
      if (grouping) {
        const ts = item.type === 'thread' && item.thread
          ? item.thread.latestTimestamp
          : new Date(this.getItemTimestamp(item));
        const bucket = this.getDateBucket(ts);
        if (bucket !== currentBucket) {
          currentBucket = bucket;
          result.push({ type: 'group', groupLabel: bucket });
        }
      }
      result.push(item);
      if (item.type === 'thread' && item.thread?.isExpanded) {
        const children = childrenOf.get(item.thread.threadId) || [];
        result.push(...children);
      }
    }

    return result;
  }

  /** Date bucket for list grouping — compares calendar days, not 24h spans. */
  private getDateBucket(ts: Date): string {
    if (isNaN(ts.getTime())) return 'Earlier';
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    const startOfWeek = new Date(startOfToday);
    startOfWeek.setDate(startOfWeek.getDate() - 6);

    if (ts >= startOfToday) return 'Today';
    if (ts >= startOfYesterday) return 'Yesterday';
    if (ts >= startOfWeek) return 'This Week';
    return 'Earlier';
  }

  /**
   * Get the sort timestamp for a display item.
   */
  private getItemTimestamp(item: DisplayItem): string {
    const msg = item.message;
    if (!msg) return '';
    return this.sortMode === 'received' ? msg.ReceivedAt as unknown as string : msg.__mj_UpdatedAt as unknown as string;
  }

  /**
   * Count of messages loaded from the main query (excludes injected thread siblings).
   */
  get displayItemCount(): number {
    return this.queryLoadedCount;
  }

  /**
   * Toggle expand/collapse for a thread group.
   */
  toggleThread(group: ThreadGroup): void {
    if (this.expandedThreads.has(group.threadId)) {
      this.expandedThreads.delete(group.threadId);
    } else {
      this.expandedThreads.add(group.threadId);
    }
  }

  /**
   * Select a message from a DisplayItem click.
   */
  async selectMessageFromItem(message: MessageListItem): Promise<void> {
    const index = this.messages.findIndex(m => m.ID === message.ID);
    if (index >= 0) {
      this.selectMessage(message, index);
    } else {
      // Message is not in the current filtered list (e.g., different status queue).
      // Load it directly by ID and display it in the detail panel.
      this.isDetailLoading = true;
      try {
        const entity = await this.loadMessageEntity(message.ID);
        if (entity) {
          this.selectedMessage = entity;
          this.selectedIndex = -1;
          this.editedContent = entity.GeneratedReplyContent || '';
          this.feedbackText = entity.ApprovalFeedback || '';
          this.replyViewMode = 'approved';
          if (entity.ApprovedReplyContent) {
            this.computeHtmlDiff(entity.GeneratedReplyContent || '', entity.ApprovedReplyContent || '');
          }
          this.loadThreadContext(entity);
        }
      } catch (error) {
        console.error('Error loading cross-filter message:', error);
      } finally {
        this.isDetailLoading = false;
        this.syncMessagePresence();
      }
    }
  }

  /**
   * TrackBy function for displayItems ngFor — prevents DOM destruction/recreation
   * when the displayItems getter returns new object references.
   */
  trackDisplayItem(index: number, item: DisplayItem): string {
    if (item.type === 'group') {
      return 'group-' + item.groupLabel;
    }
    if (item.type === 'thread' && item.thread) {
      return 'thread-' + item.thread.threadId;
    }
    return item.message?.ID ?? ('idx-' + index);
  }

  /**
   * Returns true if this item is the last child of its parent thread in the display list.
   */
  isLastThreadChild(item: DisplayItem, currentIndex: number): boolean {
    if (!item.parentThread) return false;
    const items = this.displayItems;
    if (currentIndex >= items.length - 1) return true;
    const next = items[currentIndex + 1];
    return !(next.isThreadChild && next.parentThread?.threadId === item.parentThread.threadId);
  }

  /**
   * Find the ThreadGroup containing the given message.
   */
  private getThreadGroupForMessage(message: MessageListItem | IzzyChannelMessageEntity): ThreadGroup | null {
    if (!message.ThreadID) return null;
    const item = this.displayItems.find(
      i => i.type === 'thread' && i.thread?.threadId === message.ThreadID
    );
    return item?.thread || null;
  }

  /**
   * Check if text contains search query (for highlighting)
   */
  containsSearch(text: string | null | undefined): boolean {
    if (!text || !this.isSearchActive) return false;
    return ChannelMessageSearchMatches(text, this.currentSearch());
  }

  /**
   * Highlight search matches in text
   */
  highlightSearch(text: string | null | undefined): string {
    if (!text || !this.searchQuery.trim()) return text || '';

    const query = this.searchQuery.trim();
    const regex = new RegExp(`(${this.escapeRegExp(query)})`, 'gi');
    return text.replace(regex, '<mark class="search-highlight">$1</mark>');
  }

  private escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // ========== Auto-advance Change ==========

  onAutoAdvanceChange(): void {
    this.saveUserSettings();
    this.updateUserState();
  }

  // ========== Spam Filter Change ==========

  onHideSpamChange(): void {
    this.saveUserSettings();
    this.updateUserState();
    this.refreshStatsAndData();
  }

  // ========== Queue Filter ==========

  setQueueFilter(filter: 'mine' | 'unassigned' | 'all'): void {
    if (this.queueFilter !== filter || this.assigneeFilterContactId) {
      this.queueFilter = filter;
      this.assigneeFilterContactId = null;

      this.clearSelection();
      this.deselectMessage();
      this.saveUserSettings();
      this.updateUserState();
      this.refreshStatsAndData();
    }
  }

  setNotesFilter(filter: NotesFilter): void {
    if (this.notesFilter === filter) return;
    this.notesFilter = filter;

    this.clearSelection();
    this.deselectMessage();
    this.saveUserSettings();
    this.updateUserState();
    this.refreshStatsAndData();
  }

  filterByAssignee(contactId: string): void {
    this.assigneeFilterContactId = contactId || null;
    this.queueFilter = 'all';

    this.clearSelection();
    this.deselectMessage();
    this.saveUserSettings();
    this.updateUserState();
    this.refreshStatsAndData();
  }

  // ========== Assignment ==========

  /**
   * Load team members for the currently selected organization.
   * Also determines if the current user is an admin (can assign to others).
   */
  private async loadOrgTeamMembers(): Promise<void> {
    if (!this.selectedOrganization) {
      this.orgTeamMembers = [];
      this.isCurrentUserAdmin = false;
      return;
    }

    const orgId = this.selectedOrganization.ID;
    const currentContactId = IzzyMetadataEngine.Instance.CurrentContactID;
    const orgContacts = IzzyMetadataEngine.Instance.OrganizationContacts
      .filter(oc => oc.OrganizationID === orgId && IzzyMetadataEngine.Instance.IsActiveMembership(oc));

    // Admin check: user must have Level >= ADMIN in the selected org.
    // Delegates to BCMembershipService.HasMembershipLevel — single server-side
    // query with RLS applied (the post-BAC OrganizationPersonRole row has no
    // denormalized role-name column; Level is the canonical signal).
    if (currentContactId) {
      this.isCurrentUserAdmin = await BCMembershipService.HasMembershipLevel(
        currentContactId,
        orgId,
        BC_ROLE_LEVELS.ADMIN,
        Metadata.Provider.CurrentUser
      );
    } else {
      this.isCurrentUserAdmin = false;
    }

    // Load Contact records for display names (FirstName + LastName)
    const contactIds = orgContacts.map(oc => oc.PersonID);
    if (contactIds.length === 0) {
      this.orgTeamMembers = [];
      return;
    }

    try {
      const rv = new RunView();
      const result = await rv.RunView<{ ID: string; FirstName: string; LastName: string }>({
        EntityName: 'MJ_BizApps_Common: People',
        ExtraFilter: `ID IN (${contactIds.map(id => `'${id}'`).join(',')})`,
        ResultType: 'simple',
        Fields: ['FirstName', 'LastName']
      });

      if (result?.Success) {
        const contactMap = new Map(result.Results.map(c => [c.ID, `${c.FirstName} ${c.LastName}`]));
        this.orgTeamMembers = orgContacts.map(oc => ({
          contactId: oc.PersonID,
          name: contactMap.get(oc.PersonID) || oc.PersonID
        }));
      } else {
        // Fallback to contact ID if query fails
        this.orgTeamMembers = orgContacts.map(oc => ({
          contactId: oc.PersonID,
          name: oc.PersonID
        }));
      }
    } catch (error) {
      console.error('Error loading team member names:', error);
      this.orgTeamMembers = orgContacts.map(oc => ({
        contactId: oc.PersonID,
        name: oc.PersonID
      }));
    }
  }

  /**
   * Resolve a contact ID to a display name using the local orgTeamMembers data.
   * The view join virtual field (AssignedToContact) returns email, so we look up
   * FirstName + LastName from the pre-fetched team members instead.
   */
  getContactName(contactId: string | null | undefined): string {
    if (!contactId) return '';
    const member = this.orgTeamMembers.find(m => m.contactId === contactId);
    return member?.name || 'Assigned';
  }

  /**
   * Get the display name for the assign dropdown button on the selected message.
   */
  getAssignedDisplayName(): string {
    if (!this.selectedMessage) return 'Assign';
    const contactId = this.selectedMessage.Get('AssignedToPersonID');
    if (!contactId) return 'Assign';
    return this.getContactName(contactId);
  }

  /**
   * Assign the selected (or specified) message to the current user.
   */
  async assignToMe(message?: ChannelMessageEntityExtendedClient): Promise<void> {
    const msg = message || this.selectedMessage;
    if (!msg) return;

    const myContactId = IzzyMetadataEngine.Instance.CurrentContactID;
    if (!myContactId) return;

    this.isSaving = true;
    try {
      msg.AssignTo(myContactId);
      this.stampPlannedCC(msg);
      await msg.Save();
      this.showSuccess('Assigned to you');

      // Update list item in place
      const member = this.orgTeamMembers.find(m => m.contactId === myContactId);
      const listItem = this.messages.find(m => m.ID === msg.ID);
      if (listItem) {
        listItem.AssignedToPersonID = myContactId;
        listItem.AssignedToContact = member?.name || '';
      }
    } catch (error) {
      console.error('Error assigning message:', error);
      this.errorMessage = 'Failed to assign message. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Assign a message to a specific contact (admin only).
   */
  async assignToContact(message: ChannelMessageEntityExtendedClient, contactId: string): Promise<void> {
    this.isSaving = true;
    try {
      message.AssignTo(contactId);
      this.stampPlannedCC(message);
      await message.Save();

      const member = this.orgTeamMembers.find(m => m.contactId === contactId);
      this.showSuccess(`Assigned to ${member?.name || 'team member'}`);
      this.isAssignDropdownOpen = false;

      // Update list item
      const listItem = this.messages.find(m => m.ID === message.ID);
      if (listItem) {
        listItem.AssignedToPersonID = contactId;
        listItem.AssignedToContact = member?.name || '';
      }
    } catch (error) {
      console.error('Error assigning message:', error);
      this.errorMessage = 'Failed to assign message. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Remove assignment from a message.
   */
  async unassignMessage(message?: ChannelMessageEntityExtendedClient): Promise<void> {
    const msg = message || this.selectedMessage;
    if (!msg) return;

    this.isSaving = true;
    try {
      msg.Unassign();
      this.stampPlannedCC(msg);
      await msg.Save();
      this.showSuccess('Unassigned');

      const listItem = this.messages.find(m => m.ID === msg.ID);
      if (listItem) {
        listItem.AssignedToPersonID = null;
        listItem.AssignedToContact = null;
      }
    } catch (error) {
      console.error('Error unassigning message:', error);
      this.errorMessage = 'Failed to unassign message. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Sort Mode Change ==========

  onSortModeChange(): void {

    this.saveUserSettings();
    this.updateUserState();
    this.loadData();
  }

  /**
   * Get the appropriate timestamp to display based on sort mode
   */
  getDisplayTimestamp(message: MessageListItem): Date | string {
    return this.sortMode === 'received' ? message.ReceivedAt : message.__mj_UpdatedAt;
  }

  // ========== Pagination ==========

  // ========== User Settings Persistence ==========

  private async saveUserSettings(): Promise<void> {
    try {
      const md = new Metadata();
      const settings = {
        generationStatusFilter: this.generationStatusFilter,
        approvalStatusFilter: this.approvalStatusFilter,
        isPanelCollapsed: this.isPanelCollapsed,
        panelWidth: this.panelWidth,
        autoAdvance: this.autoAdvance,
        hideSpam: this.hideSpam,
        sortMode: this.sortMode,
        queueFilter: this.queueFilter,
        notesFilter: this.notesFilter
      };

      if (!this.userSetting) {
        this.userSetting = await md.GetEntityObject<MJUserSettingEntity>('MJ: User Settings');
        this.userSetting.Setting = this.userSettingsKey;
        this.userSetting.UserID = md.CurrentUser.ID;
      }

      this.userSetting.Value = JSON.stringify(settings);
      await this.userSetting.Save();
    } catch (error) {
      console.error('MessageApproval: Error saving user settings:', error);
    }
  }

  private async loadUserSettings(): Promise<void> {
    try {
      const md = new Metadata();
      const currentUserId = md.CurrentUser.ID;

      const rv = new RunView();
      const result = await rv.RunView<MJUserSettingEntity>({
        EntityName: 'MJ: User Settings',
        ExtraFilter: `Setting = '${this.userSettingsKey}' AND UserID = '${currentUserId}'`,
        ResultType: 'entity_object'
      });

      if (result?.Success && result.Results.length > 0) {
        this.userSetting = result.Results[0];
        const settings = JSON.parse(this.userSetting.Value || '{}');

        // Apply settings
        if (settings.generationStatusFilter) this.generationStatusFilter = settings.generationStatusFilter;
        if (settings.approvalStatusFilter) this.approvalStatusFilter = settings.approvalStatusFilter;
        if (settings.isPanelCollapsed !== undefined) this.isPanelCollapsed = settings.isPanelCollapsed;
        if (settings.panelWidth) this.panelWidth = settings.panelWidth;
        if (settings.autoAdvance !== undefined) this.autoAdvance = settings.autoAdvance;
        if (settings.hideSpam !== undefined) this.hideSpam = settings.hideSpam;
        if (settings.sortMode) this.sortMode = settings.sortMode;
        if (settings.queueFilter) this.queueFilter = settings.queueFilter;
        if (settings.notesFilter) this.notesFilter = settings.notesFilter;
      }
    } catch (error) {
      console.error('MessageApproval: Error loading user settings:', error);
    }
  }

  // ========== Open Record ==========

  openMessageRecord(message?: IzzyChannelMessageEntity): void {
    const msg = message || this.selectedMessage;
    if (!msg) return;

    this.openRecord.emit({
      EntityName: 'Channel Messages',
      RecordPKey: CompositeKey.FromID(msg.ID)
    });

    this.interaction.emit({
      type: 'openMessage',
      messageId: msg.ID
    });
  }

  /**
   * Open the AI Agent Run record in a new tab (for system developers)
   */
  openAgentRun(agentRunId: string): void {
    if (!agentRunId) return;

    this.navigationService.OpenEntityRecord(
      'MJ: AI Agent Runs',
      CompositeKey.FromID(agentRunId)
    );
  }

  // ========== Utility Methods ==========

  /**
   * Renders markdown content to sanitized HTML
   */
  renderMarkdown(content: string | null | undefined): SafeHtml {
    if (!content) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }
    const html = marked.parse(content, { async: false }) as string;
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  formatDate(date: Date | string): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  /**
   * Compact list-row date (email-client pattern): time for today,
   * "Yesterday", month + day within the year, else a short date.
   * Full timestamp stays available via the row tooltip.
   */
  formatListDate(date: Date | string): string {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);

    if (d >= startOfToday) {
      return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
    if (d >= startOfYesterday) {
      return 'Yesterday';
    }
    if (d.getFullYear() === now.getFullYear()) {
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
    return d.toLocaleDateString([], { month: 'numeric', day: 'numeric', year: '2-digit' });
  }

  /** Skip category for the escalated bar: kebab-case → Title Case, with the AI-refusal category named plainly. */
  formatSkipCategory(category: string | null): string {
    if (!category) return '';
    if (category.toLowerCase() === 'model-content-blocked') return 'AI Model Refused';
    return category
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * First sentence of the escalation reason for the compact bar (markdown markers stripped).
   * The full reason renders in the viewer's card; this is the one-line progression step.
   */
  firstSentence(text: string | null | undefined): string {
    if (!text) return '';
    const plain = text.replace(/[*_`#>]/g, '').replace(/\s+/g, ' ').trim();
    const m = /^(.+?[.!?])(\s|$)/.exec(plain);
    const sentence = (m ? m[1] : plain).trim();
    return sentence.length > 220 ? sentence.slice(0, 217) + '…' : sentence;
  }

  formatRelativeTime(date: Date | string): string {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  }

  truncateText(text: string, maxLength: number = 100): string {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }

  stripHtmlTags(html: string): string {
    if (!html) return '';
    const cleaned = html.replace(/<!--[\s\S]*?-->/g, '');
    const tmp = document.createElement('div');
    tmp.innerHTML = cleaned;
    const text = (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
    return text;
  }

  /**
   * Get a user-friendly empty state message
   */
  getEmptyStateMessage(): string {
    if (this.searchQuery) {
      return 'No messages match your search';
    }

    // Queue filter context
    const queuePart = this.queueFilter === 'mine' ? ' assigned to you' :
                       this.queueFilter === 'unassigned' ? ' unassigned' : '';

    // Build a natural message based on filters (v2.0: uses generationStatusFilter)
    const statusPart = this.generationStatusFilter !== 'All' ? this.generationStatusFilter.toLowerCase() : '';
    const approvalPart = this.approvalStatusFilter !== 'All' ? this.approvalStatusFilter.toLowerCase() : '';

    if (statusPart && approvalPart) {
      return `No ${statusPart} messages with ${approvalPart} approval status${queuePart}`;
    } else if (statusPart) {
      return `No ${statusPart} messages${queuePart}`;
    } else if (approvalPart) {
      if (approvalPart === 'not required') {
        return `No messages requiring approval${queuePart}`;
      }
      return `No ${approvalPart} messages${queuePart}`;
    }

    return `No messages${queuePart}`;
  }

  getApprovalStatusClass(status: string | null): string {
    switch (status) {
      case 'Pending': return 'approval-pending';
      case 'Approved': return 'approval-approved';
      case 'Rejected': return 'approval-rejected';
      case 'Not Required': return 'approval-not-required';
      case 'Escalated': return 'approval-escalated';
      default: return '';
    }
  }

  /** Generation state is only worth a chip when it isn't the normal 'Generated' path. */
  isAbnormalGeneration(status: string | null): boolean {
    return !!status && status !== 'Generated';
  }

  getGenerationStatusClass(status: string): string {
    // v2.0: Updated to use new GenerationStatus values
    switch (status) {
      case 'Read': return 'status-read';
      case 'Creating Reply': return 'status-creating-reply';
      case 'Generated': return 'status-generated';
      case 'Skipped': return 'status-skipped';
      case 'Failed': return 'status-failed';
      case 'Manual': return 'status-manual';
      default: return '';
    }
  }

  /**
   * Determines if an error type is a send-related error (vs AI generation error)
   */
  isSendError(errorType: string | null): boolean {
    if (!errorType) return false;
    const sendErrors = ['SendFailure', 'CredentialFailure', 'Timeout',
                        'RateLimited', 'ProviderNotFound', 'ChannelConfigFailure', 'ValidationFailure'];
    return sendErrors.includes(errorType);
  }

  /**
   * Formats error type from PascalCase to readable format
   */
  formatErrorType(errorType: string | null): string {
    if (!errorType) return '';
    // Convert PascalCase to Title Case with spaces
    return errorType.replace(/([A-Z])/g, ' $1').trim();
  }

  /**
   * Friendly tooltip for list-item error badges — uses the categorizer so the
   * tooltip reflects the real underlying cause (invalid key, rate limit, etc.)
   * instead of just the generic ErrorType name.
   */
  friendlyErrorTooltip(message: ChannelMessageEntityExtendedClient): string {
    if (!message.ErrorType && !message.ErrorMessage) return '';
    const friendly = categorizeError(message.ErrorType, message.ErrorMessage);
    return friendly.action ? `${friendly.title} — ${friendly.summary}\n${friendly.action}` : `${friendly.title} — ${friendly.summary}`;
  }

  /**
   * Get CSS class for confidence score coloring
   * High (85+): green, Medium (60-84): yellow/orange, Low (0-59): red
   */
  getConfidenceScoreClass(score: number | null): string {
    if (score === null || score === undefined) return '';
    if (score >= 85) return 'confidence-high';
    if (score >= 60) return 'confidence-medium';
    return 'confidence-low';
  }

  /**
   * Get the auto-approve threshold from settings
   * Returns the configured threshold or default of 90
   */
  getAutoApproveThreshold(): number {
    // Try to get from channel settings first, then organization, then default
    if (this.selectedChannel) {
      const result = IzzyMetadataEngine.Instance.GetSettingValue('MinConfidenceForAutoApprove', this.selectedChannel.ID);
      if (result.value) {
        const parsed = parseInt(result.value, 10);
        if (!isNaN(parsed)) return parsed;
      }
    }
    return 90; // Default threshold
  }

  // ========== Action Button State Helpers ==========

  /**
   * Check if the message is still being generated (not ready for approval actions)
   */
  isMessageGenerating(): boolean {
    if (!this.selectedMessage) return true;
    const status = this.selectedMessage.GenerationStatus;
    return status === 'Read' || status === 'Creating Reply';
  }

  /**
   * Check if the Approve button should be enabled
   * - Must be Pending or Escalated approval status
   * - Must have completed generation (Generated, Skipped, or Failed)
   * - Must have reply content to approve (GeneratedReplyContent or ApprovedReplyContent)
   * - For Failed/Skipped messages, must have ApprovedReplyContent (edited manually)
   */
  canApproveMessage(): boolean {
    // The rules live on the entity (IsApprovable) — business logic stays out of the UI
    return !!this.selectedMessage && this.selectedMessage.IsApprovable;
  }

  /**
   * Get tooltip explaining why Approve is disabled
   */
  getApproveDisabledReason(): string {
    if (!this.selectedMessage) return '';
    if (this.selectedMessage.ApprovalStatus !== 'Pending' &&
        this.selectedMessage.ApprovalStatus !== 'Escalated') {
      return `Already ${this.selectedMessage.ApprovalStatus.toLowerCase()}`;
    }
    if (this.isMessageGenerating()) {
      return 'Wait for reply generation to complete';
    }

    // Check if there's any reply content
    const hasReplyContent = !!this.selectedMessage.GeneratedReplyContent || !!this.selectedMessage.ApprovedReplyContent;
    if (!hasReplyContent) {
      return 'No reply content to approve - edit the message first';
    }

    // For Failed or Skipped, need manually edited content
    if ((this.selectedMessage.GenerationStatus === 'Failed' || this.selectedMessage.GenerationStatus === 'Skipped') &&
        !this.selectedMessage.ApprovedReplyContent) {
      return 'Edit the message first to approve a skipped/failed generation';
    }
    return '';
  }

  /**
   * Check if the Reject button should be enabled
   * - Must be Pending or Escalated approval status
   * - Must have completed generation
   */
  canRejectMessage(): boolean {
    return !!this.selectedMessage && this.selectedMessage.IsRejectable;
  }

  /**
   * Get tooltip explaining why Reject is disabled
   */
  getRejectDisabledReason(): string {
    if (!this.selectedMessage) return '';
    if (this.selectedMessage.ApprovalStatus !== 'Pending' &&
        this.selectedMessage.ApprovalStatus !== 'Escalated') {
      return `Already ${this.selectedMessage.ApprovalStatus.toLowerCase()}`;
    }
    if (this.isMessageGenerating()) {
      return 'Wait for reply generation to complete';
    }
    return '';
  }

  /**
   * Check if the Edit button should be enabled
   * - Must be Pending or Escalated approval status
   * - Cannot be currently generating
   * - Allow editing even if no GeneratedReplyContent (user can manually create message)
   */
  canEditMessage(): boolean {
    if (!this.selectedMessage) return false;
    if (this.selectedMessage.ApprovalStatus !== 'Pending' && this.selectedMessage.ApprovalStatus !== 'Escalated') return false;
    if (this.isMessageGenerating()) return false;

    return true;
  }

  /**
   * Get tooltip explaining why Edit is disabled
   */
  getEditDisabledReason(): string {
    if (!this.selectedMessage) return '';
    if (this.selectedMessage.ApprovalStatus !== 'Pending' && this.selectedMessage.ApprovalStatus !== 'Escalated') {
      return `Cannot edit ${this.selectedMessage.ApprovalStatus.toLowerCase()} messages`;
    }
    if (this.isMessageGenerating()) {
      return 'Wait for reply generation to complete';
    }
    return '';
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    this.notificationMessage = message;
    this.showStateUpdateNotification = true;
    setTimeout(() => {
      this.successMessage = '';
      this.notificationMessage = '';
      this.showStateUpdateNotification = false;
    }, 3000);
  }

  // ========== Swipe Gesture Handlers (Mobile) ==========

  /**
   * Handle touch start event for swipe gestures
   */
  onTouchStart(event: TouchEvent, message: MessageListItem): void {
    // Only handle swipe on pending messages that can be approved
    if (!this.canApprove(message)) return;

    this.swipeStartX = event.touches[0].clientX;
    this.swipeCurrentX = this.swipeStartX;
    this.swipingMessageId = message.ID;
    this.swipeState[message.ID] = null;
  }

  /**
   * Handle touch move event for swipe gestures
   */
  onTouchMove(event: TouchEvent, message: MessageListItem): void {
    if (this.swipingMessageId !== message.ID) return;
    if (!this.canApprove(message)) return;

    this.swipeCurrentX = event.touches[0].clientX;
    const deltaX = this.swipeCurrentX - this.swipeStartX;

    // Determine swipe direction
    if (deltaX > 20) {
      this.swipeState[message.ID] = 'right';  // Approve
    } else if (deltaX < -20) {
      this.swipeState[message.ID] = 'left';   // Reject
    } else {
      this.swipeState[message.ID] = null;
    }
  }

  /**
   * Handle touch end event - complete swipe action if threshold met
   */
  async onTouchEnd(event: TouchEvent, message: MessageListItem): Promise<void> {
    if (this.swipingMessageId !== message.ID) return;

    const deltaX = this.swipeCurrentX - this.swipeStartX;
    const direction = this.swipeState[message.ID];

    // Check if swipe exceeded threshold
    if (Math.abs(deltaX) >= this.swipeThreshold && this.canApprove(message)) {
      if (direction === 'right') {
        await this.approveMessage(message.ID);
      } else if (direction === 'left') {
        await this.rejectMessage(message.ID);
      }
    }

    // Reset swipe state
    this.swipingMessageId = null;
    this.swipeState[message.ID] = null;
    this.swipeStartX = 0;
    this.swipeCurrentX = 0;
  }

  /**
   * Get the CSS transform for the swipe animation
   */
  getSwipeTransform(messageId: string): string {
    if (this.swipingMessageId !== messageId) {
      return 'translateX(0)';
    }

    const deltaX = this.swipeCurrentX - this.swipeStartX;
    // Limit the visual displacement
    const clampedDelta = Math.max(-this.swipeMaxDistance, Math.min(this.swipeMaxDistance, deltaX));
    return `translateX(${clampedDelta}px)`;
  }

  // ========== Reprocess ==========

  /**
   * Whether the selected message can be reprocessed.
   * Only Failed or stuck (Creating Reply) messages are eligible.
   */
  canReprocessMessage(): boolean {
    if (!this.selectedMessage) return false;
    return this.selectedMessage.CanReprocess();
  }

  /**
   * Trigger reprocessing of a failed or stuck message.
   * Shows a confirmation dialog, then calls executeReprocessMessage().
   */
  async reprocessMessage(): Promise<void> {
    if (!this.selectedMessage || this.isReprocessing) return;
    const status = this.selectedMessage.GenerationStatus;
    const statusLabel = status === 'Failed' ? 'failed' : 'stuck';
    this.showConfirmDialog(
      'Reprocess Message',
      `Are you sure you want to reprocess this ${statusLabel} message? The AI agent will re-run to generate a new reply.`,
      async () => this.executeReprocessMessage(),
      'Reprocess',
      false
    );
  }

  private async executeReprocessMessage(): Promise<void> {
    if (!this.selectedMessage) return;
    this.isReprocessing = true;
    this.errorMessage = '';
    try {
      // Reset fields to trigger reprocessing on the server side.
      // The server-side Save() override detects Failed/Creating Reply/Skipped/Generated → Read
      // and fires off the reprocessing pipeline asynchronously.
      this.selectedMessage.ResetForReprocessing();

      const saveResult = await this.selectedMessage.Save();
      if (saveResult) {
        this.showSuccess('Message queued for reprocessing');
        this.interaction.emit({
          type: 'approvalChange',
          messageId: this.selectedMessage.ID,
          newStatus: 'Pending'
        });
        await this.loadStats();
      } else {
        this.errorMessage = 'Failed to save message state for reprocessing.';
      }
    } catch (error) {
      console.error('Error reprocessing message:', error);
      this.errorMessage = 'Failed to reprocess message. Please try again.';
    } finally {
      this.isReprocessing = false;
    }
  }

  // ========== Rescore ==========

  canRescoreMessage(): boolean {
    if (!this.selectedMessage) return false;
    if (this.isMessageGenerating()) return false; // UI state concern
    return this.selectedMessage.CanRescore();
  }

  getRescoreDisabledReason(): string {
    if (!this.selectedMessage) return '';
    if (this.isMessageGenerating()) return 'Wait for reply generation to complete';
    if (!this.selectedMessage.CanRescore()) {
      if (!this.selectedMessage.GeneratedReplyContent) return 'No generated reply to evaluate';
      if (!this.selectedMessage.AIAgentRunID) return 'No stored agent run — cannot rescore older messages without payload data';
    }
    return '';
  }

  async rescoreMessage(): Promise<void> {
    if (!this.selectedMessage || !this.canRescoreMessage()) return;
    this.showConfirmDialog(
      'Re-evaluate Confidence',
      'Re-run the AI confidence evaluator with the current rubric. This will update the score but not change the generated reply.',
      async () => this.executeRescoreMessage(),
      'Re-evaluate',
      false
    );
  }

  private async executeRescoreMessage(): Promise<void> {
    if (!this.selectedMessage) return;
    const messageId = this.selectedMessage.ID;
    this.isReprocessing = true;
    this.rescoringMessageIds.add(messageId);
    this.errorMessage = '';
    try {
      // Delegate to entity subclass method for consistent behavior
      this.selectedMessage.TriggerRescore();

      const saveResult = await this.selectedMessage.Save();
      if (!saveResult) {
        this.errorMessage = 'Failed to save message state for re-evaluation.';
        this.isReprocessing = false;
        this.rescoringMessageIds.delete(messageId);
        return;
      }

      // Poll for results — server-side rescore runs asynchronously
      await this.pollForRescoreResult(messageId);
    } catch (error) {
      console.error('Error rescoring message:', error);
      this.errorMessage = 'Failed to re-evaluate message. Please try again.';
      this.isReprocessing = false;
      this.rescoringMessageIds.delete(messageId);
    }
  }

  private async pollForRescoreResult(messageId: string): Promise<void> {
    const POLL_INTERVAL = 2500; // ms
    const MAX_POLLS = 24; // 60 seconds total
    let polls = 0;

    const poll = async () => {
      polls++;
      try {
        const rv = new RunView();
        const result = await rv.RunView({
          EntityName: 'Channel Messages',
          ExtraFilter: `ID = '${messageId.replace(/'/g, "''")}'`,
          ResultType: 'entity_object',
        }, new Metadata().CurrentUser);

        const entity = result?.Results?.[0] as IzzyChannelMessageEntity | undefined;
        if (entity?.AIConfidenceScore !== null && entity?.AIConfidenceScore !== undefined) {
          // Rescore complete — update the selected message with fresh data
          if (this.selectedMessage?.ID === messageId) {
            this.selectedMessage.AIConfidenceScore = entity.AIConfidenceScore;
            this.selectedMessage.AIConfidenceReason = entity.AIConfidenceReason;
            this.selectedMessage.AIEvaluatedAt = entity.AIEvaluatedAt;
            this.selectedMessage.ApprovalStatus = entity.ApprovalStatus;
          }
          this.isReprocessing = false;
          this.rescoringMessageIds.delete(messageId);
          this.showSuccess('Re-evaluation complete');
          return;
        }
      } catch (err) {
        console.error('Error polling for rescore result:', err);
      }

      if (polls >= MAX_POLLS) {
        this.isReprocessing = false;
        this.rescoringMessageIds.delete(messageId);
        this.showSuccess('Re-evaluation is still processing. Refresh to see updated score.');
        return;
      }

      setTimeout(() => poll(), POLL_INTERVAL);
    };

    await poll();
  }

  async bulkRescore(): Promise<void> {
    if (this.selectedIds.size === 0) return;
    this.showConfirmDialog(
      'Bulk Re-evaluate',
      `Re-run AI confidence evaluation on ${this.selectedIds.size} message${this.selectedIds.size === 1 ? '' : 's'}? This will update scores but not change generated replies.`,
      async () => this.executeBulkRescore(),
      'Re-evaluate All',
      false
    );
  }

  private async executeBulkRescore(): Promise<void> {
    this.isSaving = true;
    this.isReprocessing = true;
    try {
      const idsToRescore = this.messages
        .filter(m => this.selectedIds.has(m.ID))
        .map(m => m.ID);

      const rescoredIds: string[] = [];
      const skippedCount = { noLoad: 0, notEligible: 0 };
      for (const id of idsToRescore) {
        const msg = await this.loadMessageEntity(id);
        if (!msg) { skippedCount.noLoad++; continue; }
        if (!msg.CanRescore()) { skippedCount.notEligible++; continue; }

        msg.TriggerRescore();
        await msg.Save();
        rescoredIds.push(id);
        this.rescoringMessageIds.add(id);
      }

      const skippedTotal = skippedCount.noLoad + skippedCount.notEligible;
      let toastMsg = `${rescoredIds.length} message${rescoredIds.length === 1 ? '' : 's'} queued for re-evaluation`;
      if (skippedTotal > 0) {
        toastMsg += ` (${skippedTotal} skipped — not eligible)`;
      }
      this.showSuccess(toastMsg);
      this.selectedIds.clear();
      this.selectAllChecked = false;
      this.isSaving = false;

      // Poll until all rescored messages have scores, then refresh
      if (rescoredIds.length > 0) {
        await this.pollForBulkRescoreResults(rescoredIds);
      }
    } catch (error) {
      console.error('Error in bulk rescore:', error);
      this.errorMessage = 'Failed to re-evaluate some messages.';
      this.isSaving = false;
      this.isReprocessing = false;
      this.rescoringMessageIds.clear();
    }
  }

  private async pollForBulkRescoreResults(messageIds: string[]): Promise<void> {
    const POLL_INTERVAL = 3000;
    const MAX_POLLS = 20; // 60 seconds total
    let polls = 0;

    const poll = async () => {
      polls++;
      try {
        const rv = new RunView();
        const idFilter = messageIds.map(id => `'${id.replace(/'/g, "''")}'`).join(',');
        const result = await rv.RunView({
          EntityName: 'Channel Messages',
          ExtraFilter: `ID IN (${idFilter})`,
          ResultType: 'entity_object',
        }, new Metadata().CurrentUser);

        const allDone = result?.Results?.every((e: IzzyChannelMessageEntity) =>
          e.AIConfidenceScore !== null && e.AIConfidenceScore !== undefined
        );

        if (allDone) {
          this.isReprocessing = false;
          messageIds.forEach(id => this.rescoringMessageIds.delete(id));
          this.showSuccess('Bulk re-evaluation complete');
          await this.refreshStatsAndData();
          return;
        }
      } catch (err) {
        console.error('Error polling for bulk rescore results:', err);
      }

      if (polls >= MAX_POLLS) {
        this.isReprocessing = false;
        this.rescoringMessageIds.clear();
        this.showSuccess('Re-evaluation is still processing. Refresh to see updated scores.');
        await this.refreshStatsAndData();
        return;
      }

      setTimeout(() => poll(), POLL_INTERVAL);
    };

    await poll();
  }

  // ========== Re-open for Review ==========

  /**
   * Re-open a closed message (approved or rejected) for review.
   * Only allowed if the message hasn't been sent yet.
   */
  async reopenForReview(): Promise<void> {
    if (!this.selectedMessage) return;

    // Safety check: don't allow re-opening sent messages
    if (this.selectedMessage.SentContent) {
      this.errorMessage = 'Cannot re-open a message that has already been sent.';
      return;
    }

    this.showConfirmDialog(
      'Re-open for Review',
      `Are you sure you want to re-open this ${this.selectedMessage.ApprovalStatus?.toLowerCase()} message for review? It will be moved back to Pending status.`,
      async () => this.executeReopenForReview(),
      'Re-open',
      false
    );
  }

  private async executeReopenForReview(): Promise<void> {
    if (!this.selectedMessage) return;

    this.isSaving = true;
    try {
      const previousStatus = this.selectedMessage.ApprovalStatus;

      // Delegate to entity subclass method for consistent behavior
      this.selectedMessage.ReopenForReview();

      await this.selectedMessage.Save();

      const needsReprocessing = previousStatus === 'Not Required' || previousStatus === 'Rejected';
      this.showSuccess(needsReprocessing ? 'Message queued for reprocessing' : 'Message re-opened for review');

      this.interaction.emit({
        type: 'approvalChange',
        messageId: this.selectedMessage.ID,
        newStatus: 'Pending'
      });

      await this.loadStats();

      // Follow the message to Pending (same pattern as submitCompose): the row no
      // longer belongs in a non-Pending filtered list, and leaving it there makes
      // the reopen look like a no-op.
      const reopenedId = this.selectedMessage.ID;
      if (this.approvalStatusFilter !== 'All' && this.approvalStatusFilter !== 'Pending') {
        this.approvalStatusFilter = 'Pending';
      }
      // The generation filter can hide it too ('Manual' staff drafts, 'Failed'/'Read'
      // reprocess rows never match the default 'Generated') — widen it so the
      // followed message is actually visible.
      if (this.generationStatusFilter !== 'All' &&
          this.generationStatusFilter !== this.selectedMessage.GenerationStatus) {
        this.generationStatusFilter = 'All';
      }
      await this.loadData();
      const index = this.messages.findIndex(m => m.ID === reopenedId);
      if (index >= 0) {
        await this.selectMessage(this.messages[index], index);
      }
    } catch (error) {
      console.error('Error re-opening message:', error);
      this.errorMessage = 'Failed to re-open message. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Check if a message can be retried
   */
  canRetrySend(message: ChannelMessageEntityExtendedClient): boolean {
    if (!message) return false;
    return message.CanRetrySend();
  }

  /**
   * Retry sending a failed message
   */
  async retrySendMessage(): Promise<void> {
    if (!this.selectedMessage || !this.canRetrySend(this.selectedMessage)) {
      return;
    }

    this.isSaving = true;
    this.errorMessage = null;

    try {
      // Delegate to entity subclass method for consistent behavior
      this.selectedMessage.RetrySend();

      // Save triggers sendApprovedMessage() via ChannelMessageEntityExtended.Save()
      await this.selectedMessage.Save();

      // Check result
      if (this.selectedMessage.SentContent) {
        this.showSuccess('Message sent successfully');
        await this.loadStats(); // Refresh stats
      } else if (this.selectedMessage.ErrorType) {
        this.errorMessage = `Send failed: ${this.getUserFriendlyErrorMessage(
          this.selectedMessage.ErrorType,
          this.selectedMessage.ErrorMessage
        )}`;
      }
    } catch (error: any) {
      this.errorMessage = `Failed to retry: ${error.message || 'Unknown error'}`;
      console.error('Error retrying send:', error);
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Get user-friendly error message for display
   */
  getUserFriendlyErrorMessage(errorType: string | null, errorMessage: string | null): string {
    const friendlyMessages: { [key: string]: string } = {
      'SendFailure': 'Unable to deliver message. This may be a temporary issue with the email service.',
      'CredentialFailure': 'Email account credentials are invalid or expired. Please update your channel credentials in settings.',
      'Timeout': 'Email service timed out. This is usually temporary - please try again.',
      'RateLimited': 'Too many messages sent. Please wait a few minutes and retry.',
      'ProviderNotFound': 'Email provider configuration is missing. Please contact support.',
      'ChannelConfigFailure': 'Channel configuration is invalid. Please check your channel settings.',
      'ValidationFailure': 'Message content failed validation. Please check the message details.',
    };
    return friendlyMessages[errorType || ''] || 'An unexpected error occurred while sending.';
  }
}

/**
 * Utility function to prevent tree-shaking
 */
export function LoadMessageApprovalComponent(): void {
  // Intentionally empty - prevents tree-shaking
}
